<template>
  <div class="page-outer commerce-settings-page">
    <div class="page-header page-header-commerce-settings">
      <div class="header-left">
        <h1 class="page-title">商城設定</h1>
      </div>
    </div>

    <div class="page-container">
    <div v-if="commerceLoading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>
    <div v-else class="commerce-settings-layout">
      <div class="commerce-settings-tabs-nav" role="tablist" aria-label="商城設定分類">
        <button
          type="button"
          role="tab"
          class="commerce-settings-tab-btn"
          :class="{ active: settingsTab === 'general' }"
          :aria-selected="settingsTab === 'general'"
          @click="requestSettingsTab('general')"
        >
          <i class="ri-settings-3-line" aria-hidden="true"></i>
          一般設定
        </button>
        <button type="button" role="tab" class="commerce-settings-tab-btn" :class="{ active: settingsTab === 'integration' }" :aria-selected="settingsTab === 'integration'" @click="requestSettingsTab('integration')">
          <i class="ri-bank-card-line" aria-hidden="true"></i> 金流物流電子發票
        </button>
        <button type="button" role="tab" class="commerce-settings-tab-btn" :class="{ active: settingsTab === 'appearance' }" :aria-selected="settingsTab === 'appearance'" @click="requestSettingsTab('appearance')">
          <i class="ri-paint-brush-line" aria-hidden="true"></i> 外觀與版型
        </button>
        <button type="button" role="tab" class="commerce-settings-tab-btn" :class="{ active: settingsTab === 'menu' }" :aria-selected="settingsTab === 'menu'" @click="requestSettingsTab('menu')">
          <i class="ri-menu-line" aria-hidden="true"></i> 選單設定
        </button>
        <button type="button" role="tab" class="commerce-settings-tab-btn" :class="{ active: settingsTab === 'footer' }" :aria-selected="settingsTab === 'footer'" @click="requestSettingsTab('footer')">
          <i class="ri-layout-bottom-line" aria-hidden="true"></i> Footer 設定
        </button>
        <button type="button" role="tab" class="commerce-settings-tab-btn" :class="{ active: settingsTab === 'home' }" :aria-selected="settingsTab === 'home'" @click="requestSettingsTab('home')">首頁內容</button>
        <button type="button" role="tab" class="commerce-settings-tab-btn" :class="{ active: settingsTab === 'delivery' }" :aria-selected="settingsTab === 'delivery'" @click="requestSettingsTab('delivery')">
          <i class="ri-time-line" aria-hidden="true"></i> 收貨時段
        </button>
        <button type="button" role="tab" class="commerce-settings-tab-btn" :class="{ active: settingsTab === 'scheduled' }" :aria-selected="settingsTab === 'scheduled'" @click="requestSettingsTab('scheduled')">
          <i class="ri-calendar-check-line" aria-hidden="true"></i> 指定出貨日
        </button>
        <button
          v-if="monthlyCreditTabAvailable"
          type="button"
          role="tab"
          class="commerce-settings-tab-btn"
          :class="{ active: settingsTab === 'monthly_credit' }"
          :aria-selected="settingsTab === 'monthly_credit'"
          @click="openMonthlyCreditTab"
        >
          <i class="ri-bank-card-2-line" aria-hidden="true"></i>
          月結額度
        </button>
        <button
          type="button"
          role="tab"
          class="commerce-settings-tab-btn"
          :class="{ active: settingsTab === 'analytics' }"
          :aria-selected="settingsTab === 'analytics'"
          @click="requestSettingsTab('analytics')"
        >
          <i class="ri-bar-chart-box-line" aria-hidden="true"></i>
          分析與追蹤
        </button>
      </div>

      <div
        v-show="settingsTab === 'general' || settingsTab === 'integration'"
        class="commerce-settings-stack"
        :class="{
          'commerce-settings-stack--glued': settingsTab === 'general',
          'commerce-integration-grid': settingsTab === 'integration',
        }"
        role="tabpanel"
        :aria-label="settingsTab === 'integration' ? '金流物流電子發票' : '一般設定'"
      >
      <div v-if="settingsTab === 'general'" class="commerce-tab-toolbar">
        <div class="commerce-tab-toolbar__text">
          <h2 class="commerce-tab-toolbar__title">一般設定</h2>
          <p class="commerce-tab-toolbar__desc">品牌、網址、結帳與庫存等基本設定</p>
        </div>
        <div class="commerce-tab-toolbar__actions">
          <span v-if="commerceSaveSuccess && lastSavedScope === 'general'" class="save-feedback save-feedback--ok">
            <i class="ri-check-line"></i> 已儲存
          </span>
          <span v-if="commerceSaveError && lastSavedScope === 'general'" class="save-feedback save-feedback--err">{{ commerceSaveError }}</span>
          <span v-if="isTabDirty('general')" class="save-feedback save-feedback--dirty">未儲存</span>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="commerceSaving || !!saveValidationMessage"
            :title="saveValidationMessage || undefined"
            @click="handleScopedSave('general')"
          >
            <span v-if="commerceSaving && lastSavedScope === 'general'" class="spinner-sm"></span>
            {{ commerceSaving && lastSavedScope === 'general' ? '儲存中...' : '儲存' }}
          </button>
        </div>
      </div>
      <div v-if="settingsTab === 'general'" class="settings-card">
        <div class="settings-card-header">
          <i class="ri-store-2-line" style="color: #667eea;"></i>
          <div>
            <h2 class="settings-card-title">商城品牌</h2>
            <p class="settings-card-desc">
              設定前台頁首／頁尾顯示：有上傳 Logo 時顯示圖片；未上傳時顯示商城名稱（不再使用客戶代碼作為預設標題）。
            </p>
          </div>
        </div>
        <div class="form-body">
          <p v-if="invoiceProviders.length" class="form-hint">
            已建立 {{ invoiceProviders.length }} 組發票平台設定，預設以目前選擇之服務商為主。
          </p>
          <div class="form-group">
            <label class="form-label">商城名稱</label>
            <input
              v-model="commerceSettings.storefrontDisplayName"
              type="text"
              class="form-control"
              maxlength="200"
              placeholder="例：○○生活選物"
              autocomplete="organization"
            />
            <p class="form-hint">無 Logo 時會顯示此名稱；若名稱與 Logo 皆未設定，前台顯示為「商城」。</p>
          </div>
          <div class="form-group commerce-brand-logo-uploader">
            <label class="form-label">商城 Logo</label>
            <ImageUploader
              v-model="commerceSettings.storefrontLogoUrl"
              :upload-fn="uploadCommerceBrandImage"
              shape="free"
              hint="建議橫式 PNG／WebP，支援透明背景"
              preview-natural-ratio
              :resize-max-edge="1600"
              preserve-transparency
            />
          </div>
        </div>
      </div>
      <div v-if="settingsTab === 'general'" class="settings-card">
        <div class="settings-card-header">
          <i class="ri-global-line" style="color: #667eea;"></i>
          <div>
            <h2 class="settings-card-title">前台網址</h2>
            <p class="settings-card-desc">
              訪客實際開啟商城的網址（與瀏覽器網址列一致）。可用系統預設子網域，或改填自購網域之完整網址。
            </p>
          </div>
        </div>
        <div class="form-body">
          <FormTipBox title="前台網址設定說明" spacing="section">
            <p class="seo-help-intro">
              <strong>單一來源：</strong>訪客實際用哪個網址開商城，以本頁為準；前台
              <code>resolve-store</code> 會用瀏覽器網址的<strong>主機名</strong>對應商店。LINE 設定頁的電商網域為<strong>唯讀</strong>，與此處同步。
            </p>
            <ol class="commerce-storefront-tip-list">
              <li>
                <strong>只用平台預設子網域：</strong>上方「系統預設」會顯示依 LINE Messaging Channel ID 推算的
                <code>https://….mall.lockline.io</code>（測試環境為 <code>https://….dev-mall.lockline.io</code>）。點<strong>套用預設網址</strong>填入下方，再按本分頁<strong>儲存</strong>。
              </li>
              <li>
                <strong>自購網域：</strong>下方填<strong>完整網址</strong>（例 <code>https://www.您的網域.com</code>）。須在網域商將該主機名<strong>CNAME</strong>指向上方系統預設之 Edge 主機名（僅主機名、不含
                <code>https://</code>）；細節亦會在 LINE 設定頁的 DNS 區塊顯示。
              </li>
              <li>
                <strong>本機開發：</strong>可填 <code>http://localhost:3100</code>；租戶比對使用
                <code>localhost</code>（不含 port），port 僅寫在網址裡。
              </li>
              <li><strong>留空：</strong>表示尚未綁定自訂前台網址（仍以 API／部署預設行為為準）。</li>
            </ol>
          </FormTipBox>
          <div v-if="lineOaOptions.length > 1" class="commerce-line-oa-warn" role="status">
            <strong>您已綁定多個 LINE 官方帳號。</strong>
            請於下方選擇<strong>商城前台、預設子網域與公開商品目錄</strong>所對應的 Messaging Channel ID。
            若未指定，系統暫以清單排序第一筆為準，可能與預期不符，建議明確選定後按本分頁「儲存」。
          </div>
          <p v-else-if="lineOaOptions.length === 1" class="commerce-line-oa-auto-hint">
            您目前僅綁定一個 LINE 官方帳號，系統已自動採用其 Messaging Channel ID（<code>{{ lineOaOptions[0].messagingChannelId }}</code>）作為商城預設參考，無須另行指定。
          </p>
          <div v-if="lineOaOptions.length > 1" class="form-group">
            <label class="form-label">商城綁定的 LINE（Messaging Channel ID）</label>
            <select v-model="commerceSettings.storefrontMessagingChannelId" class="form-control form-control--narrow">
              <option value="">（未指定：依系統排序第一筆）</option>
              <option v-for="o in lineOaOptions" :key="o.messagingChannelId" :value="o.messagingChannelId">
                {{ o.label }}
              </option>
            </select>
            <p class="form-hint">
              與前台環境變數 <code>NUXT_PUBLIC_MESSAGING_CHANNEL_ID</code>、公開目錄 API 所帶之 Channel ID 應指向此帳號。
            </p>
          </div>
          <div class="form-group">
            <label class="form-label">系統預設子網域（參考）</label>
            <div class="storefront-default-row">
              <input
                :value="suggestedDefaultStorefrontUrl || '（尚無 LINE 設定或無法取得 Channel ID，請先完成 LINE 官方帳號設定）'"
                type="text"
                class="form-control storefront-default-row__readonly"
                readonly
                @click="$event.target.select()"
              />
              <button
                type="button"
                class="btn btn-secondary"
                :disabled="!suggestedDefaultStorefrontUrl"
                @click="applySuggestedStorefrontUrl"
              >
                套用預設網址
              </button>
            </div>
            <p class="form-hint">
              依您指定的商城 LINE（多帳號時以下拉選單為準；僅一個帳號時自動採用該帳號）之 Messaging Channel ID 推算（加上
              <code>.mall.lockline.io</code>，測試環境為 <code>.dev-mall.lockline.io</code>）。點「套用」會填入下方欄位，仍須按本分頁<strong>儲存</strong>才會寫入。
            </p>
          </div>
          <div class="form-group">
            <label class="form-label">實際使用之前台網址（含自購網域）</label>
            <input
              v-model="storefrontFrontUrl"
              type="text"
              class="form-control"
              placeholder="例：套用預設，或填 https://www.您的網域.com、http://localhost:3100"
              autocomplete="off"
            />
            <p class="form-hint">
              <strong>自購網域</strong>：請填完整網址（含 <code>https://</code>），並在 DNS 將該網域 CNAME 指向前台 Edge（與 LINE 設定頁說明一致）。本機開發可填
              <code>http://localhost:3100</code>。若只填網域會預設補 <code>https://</code>。留空表示未綁定前台網址。
            </p>
          </div>
        </div>
      </div>
      <div v-if="settingsTab === 'general'" class="settings-card">
        <div class="settings-card-header">
          <i class="ri-bank-card-line" style="color: #667eea;"></i>
          <div>
            <h2 class="settings-card-title">結帳與收款</h2>
            <p class="settings-card-desc">設定商城的收款方式與結帳模式</p>
          </div>
        </div>
        <div class="form-body">
          <div class="form-group">
            <label class="form-label">商城模式</label>
            <select v-model="commerceSettings.commerceMode" class="form-control">
              <option value="b2c">B2C（一般消費者）</option>
              <option value="b2b">B2B（企業客戶）</option>
            </select>
            <p v-if="!monthlyCreditTabAvailable" class="form-hint">
              月結額度僅在<strong> B2B（企業客戶）</strong>模式下可設定；選擇 B2B 後會顯示「月結額度」分頁。
            </p>
          </div>
          <div class="form-group">
            <label class="form-label">允許非會員（訪客）下單</label>
            <div class="order-capture-only-switch-row">
              <div
                class="provider-enable-group"
                title="開啟後未登入訪客可結帳；關閉則須先登入會員"
              >
                <button
                  type="button"
                  class="provider-switch"
                  :class="{ 'provider-switch--on': !commerceSettings.checkoutRequiresLogin }"
                  role="switch"
                  :aria-checked="!commerceSettings.checkoutRequiresLogin ? 'true' : 'false'"
                  :aria-label="
                    !commerceSettings.checkoutRequiresLogin
                      ? '允許非會員下單已開啟，點擊關閉'
                      : '允許非會員下單已關閉，點擊開啟'
                  "
                  @click="commerceSettings.checkoutRequiresLogin = !commerceSettings.checkoutRequiresLogin"
                />
                <span class="provider-enable-text">{{
                  !commerceSettings.checkoutRequiresLogin ? '開啟' : '關閉'
                }}</span>
              </div>
            </div>
            <p class="form-hint">
              關閉時（預設）：結帳須先登入會員，可使用點數、持有之優惠券等個人化權益。開啟後：允許未登入訪客結帳（實際購物流程與訂單欄位以前台／API 實作為準）。
              <template v-if="commerceSettings.commerceMode === 'b2b'">
                <strong>B2B 商店</strong>仍以登入後報價與月結額度為主；若開啟訪客下單，請確認營運與報價政策。
              </template>
            </p>
          </div>
          <div class="form-group">
            <label class="form-label">僅收訂單</label>
            <div class="order-capture-only-switch-row">
              <div class="provider-enable-group" title="以收單為主，不強制線上收款">
                <button
                  type="button"
                  class="provider-switch"
                  :class="{ 'provider-switch--on': commerceSettings.orderCaptureOnly }"
                  role="switch"
                  :aria-checked="commerceSettings.orderCaptureOnly ? 'true' : 'false'"
                  :aria-label="commerceSettings.orderCaptureOnly ? '僅收訂單已開啟，點擊關閉' : '僅收訂單已關閉，點擊開啟'"
                  @click="toggleOrderCaptureOnly"
                />
                <span class="provider-enable-text">{{ commerceSettings.orderCaptureOnly ? '開啟' : '關閉' }}</span>
              </div>
            </div>
            <p class="form-hint">
              開啟後表示以「收單」為主：可不經線上金流／不提供匯款資訊，訂單仍可有應付金額；實際收款與是否履約由後台人工處理（與「無需付款」之全額點數或免費品不同）。
              結帳與訂單狀態機實作階段將依此旗標分流。
            </p>
          </div>
          <div v-if="!commerceSettings.orderCaptureOnly" class="form-group">
            <label class="form-label">結算方式（可複選）</label>
            <p v-if="commerceSettings.commerceMode === 'b2c'" class="form-hint">
              <strong>月結帳單</strong>僅適用於 <strong>B2B（企業客戶）</strong>模式；商城模式為 B2C 時無法勾選。
            </p>
            <div class="settlement-modes-group">
              <label
                v-for="m in SETTLEMENT_MODE_OPTIONS"
                :key="m.value"
                class="settlement-mode-option"
                :class="{
                  'settlement-mode-option--disabled':
                    m.value === 'monthly_statement' && commerceSettings.commerceMode === 'b2c',
                }"
              >
                <input
                  type="checkbox"
                  class="toggle-input"
                  :value="m.value"
                  v-model="commerceSettings.settlementModes"
                  :disabled="m.value === 'monthly_statement' && commerceSettings.commerceMode === 'b2c'"
                />
                <div class="settlement-mode-info">
                  <span class="settlement-mode-label">{{ m.label }}</span>
                  <span class="settlement-mode-desc">{{ m.desc }}</span>
                </div>
              </label>
            </div>
            <aside
              v-if="monthlyStatementSelected"
              class="commerce-monthly-statement-tip"
              role="note"
              aria-labelledby="commerce-monthly-statement-tip-title"
            >
              <i class="commerce-monthly-statement-tip__icon ri-lightbulb-flash-line" aria-hidden="true" />
              <div class="commerce-monthly-statement-tip__body">
                <h3 id="commerce-monthly-statement-tip-title" class="commerce-monthly-statement-tip__title">
                  已啟用月結帳單
                </h3>
                <ul class="commerce-monthly-statement-tip__list">
                  <li>
                    <strong>月結額度</strong>：若尚未在「<strong>月結額度</strong>」分頁為各會員等級設定額度，代表<strong>不限制</strong>月結掛帳額度；有設定時才會依等級上限控管。
                  </li>
                  <li>
                    <strong>會員等級價格</strong>：可到
                    <router-link :to="{ name: 'ClientCommerceB2bPricing' }" class="commerce-monthly-statement-tip__link">
                      B2B 價格
                    </router-link>
                    依不同會員等級設定專屬售價；若<strong>未設定</strong> B2B 等級價，則<strong>所有會員等級</strong>適用相同價格（一般牌價／列表價）。
                  </li>
                </ul>
                <p v-if="monthlyCreditTabAvailable" class="commerce-monthly-statement-tip__actions">
                  <button type="button" class="btn btn-sm btn-primary" @click="openMonthlyCreditTab">
                    <i class="ri-bank-card-line" aria-hidden="true" />
                    前往月結額度設定
                  </button>
                </p>
              </div>
            </aside>
            <div v-if="bankTransferSelected" class="commerce-bank-transfer-block">
              <h3 class="commerce-bank-transfer-block__title">
                <i class="ri-bank-line" aria-hidden="true" />
                銀行匯款資訊
              </h3>
              <p class="form-hint commerce-bank-transfer-block__lead">
                以下欄位會顯示於<strong>前台結帳頁</strong>，供會員匯款時參考。<span class="commerce-bank-transfer-required-star">*</span>
                為必填；分行與匯款說明為選填（留空者前台不顯示該列）。
              </p>
              <p
                v-if="bankTransferValidationMessage"
                class="form-hint form-hint--error commerce-bank-transfer-block__error"
                role="alert"
              >
                <i class="ri-error-warning-line" aria-hidden="true" />
                {{ bankTransferValidationMessage }}
              </p>
              <div class="commerce-bank-transfer-grid">
                <div
                  v-for="field in BANK_TRANSFER_FIELD_DEFS"
                  :key="field.key"
                  class="form-group commerce-bank-transfer-field"
                  :class="{ 'commerce-bank-transfer-field--invalid': isBankTransferRequiredFieldEmpty(field) }"
                >
                  <label class="form-label" :for="`bank-transfer-${field.key}`">
                    <span v-if="field.required" class="commerce-bank-transfer-required-star" aria-hidden="true">*</span>
                    {{ field.label }}
                  </label>
                  <textarea
                    v-if="field.multiline"
                    :id="`bank-transfer-${field.key}`"
                    v-model="commerceSettings.bankTransfer[field.key]"
                    class="form-control"
                    rows="3"
                    :placeholder="field.placeholder"
                    :required="field.required"
                    :aria-invalid="isBankTransferRequiredFieldEmpty(field) ? 'true' : 'false'"
                    autocomplete="off"
                  />
                  <input
                    v-else
                    :id="`bank-transfer-${field.key}`"
                    v-model="commerceSettings.bankTransfer[field.key]"
                    type="text"
                    class="form-control"
                    :placeholder="field.placeholder"
                    :required="field.required"
                    :aria-invalid="isBankTransferRequiredFieldEmpty(field) ? 'true' : 'false'"
                    autocomplete="off"
                  />
                </div>
              </div>
            </div>
            <p v-if="!commerceSettings.settlementModes || commerceSettings.settlementModes.length === 0" class="form-hint form-hint--error">
              請至少選擇一種結算方式
            </p>
          </div>
        </div>
      </div>

      <div v-if="settingsTab === 'integration'" class="commerce-integration-grid__col commerce-integration-grid__col--main">
      <div class="settings-card settings-card--integration">
        <div class="settings-card-header settings-card-header--with-actions">
          <i class="ri-bank-card-line" style="color: #667eea;"></i>
          <div class="settings-card-header__main">
            <h2 class="settings-card-title">金流</h2>
            <p class="settings-card-desc">管理線上收款服務商、啟用狀態與憑證</p>
          </div>
          <div class="settings-card-header__actions">
            <span v-if="isScopeDirty('integration_payment')" class="save-feedback save-feedback--dirty">未儲存</span>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="commerceSaving"
              @click="handleScopedSave('integration_payment')"
            >
              <span v-if="commerceSaving && lastSavedScope === 'integration_payment'" class="spinner-sm"></span>
              {{ commerceSaving && lastSavedScope === 'integration_payment' ? '儲存中...' : '儲存' }}
            </button>
          </div>
        </div>
        <div v-if="!commerceSettings.orderCaptureOnly" class="form-body">
          <p v-if="!visiblePaymentProviders.length" class="commerce-empty-hint">尚無金流 Provider，可新增下方 Provider。</p>

          <div v-for="p in visiblePaymentProviders" :key="p.provider_code" class="provider-block">
            <div class="provider-row">
              <div class="provider-info">
                <span class="provider-code">{{ integrationProviderRowLabel('payment', p.provider_code, p.display_name) }}</span>
                <span class="provider-badge" :class="p.is_sandbox ? 'sandbox' : 'live'">
                  {{ p.is_sandbox ? '測試' : '正式' }}
                </span>
              </div>
              <div class="provider-actions">
                <div class="provider-enable-group" title="是否於結帳時提供此金流">
                  <button
                    type="button"
                    class="provider-switch"
                    :class="{ 'provider-switch--on': p.is_enabled }"
                    role="switch"
                    :aria-checked="p.is_enabled ? 'true' : 'false'"
                    :aria-label="p.is_enabled ? '啟用中，點擊改為停用' : '已停用，點擊改為啟用'"
                    @click="onProviderEnabledSwitch(p)"
                  />
                  <span class="provider-enable-text">{{ p.is_enabled ? '啟用' : '停用' }}</span>
                </div>
                <button
                  type="button"
                  class="btn-provider-edit"
                  @click="toggleProviderExpand(p.provider_code, 'payment')"
                  :title="expandedProvider === providerKey('payment', p.provider_code) ? '收合' : '展開'"
                >
                  <i :class="expandedProvider === providerKey('payment', p.provider_code) ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
                  {{ expandedProvider === providerKey('payment', p.provider_code) ? '收合' : '展開' }}
                </button>
                <button type="button" class="btn-provider-delete" title="刪除此金流設定" @click="handleDeleteProvider(p)">
                  <i class="ri-delete-bin-line"></i>
                  刪除
                </button>
              </div>
            </div>

            <div v-if="expandedProvider === providerKey('payment', p.provider_code)" class="provider-credentials">
              <div class="credentials-hint">
                <i class="ri-shield-keyhole-line"></i>
                憑證儲存於後端加密欄位，請勿與他人分享。
              </div>
              <div v-if="!isCatalogIntegrationProvider('payment', p.provider_code)" class="form-group provider-cred-field">
                <label class="form-label form-label--sm">顯示名稱</label>
                <input
                  v-model="p.display_name_draft"
                  class="form-control"
                  maxlength="120"
                  placeholder="選填，未填則只顯示代碼"
                  :aria-label="`${p.provider_code} 顯示名稱`"
                />
                <p class="form-hint">與憑證一起儲存。儲存後，上方標題會改成「名稱 (代碼)」。</p>
              </div>

              <div
                v-if="String(p.provider_code).toLowerCase() === 'ecpay' && p.is_sandbox && getProviderFields(p.provider_code).length"
                class="ecpay-sandbox-fill"
              >
                <button type="button" class="btn btn-secondary" @click="fillEcpaySandboxCredentials(p.provider_code)">
                  <i class="ri-flask-line"></i>
                  填入綠界官方測試參數
                </button>
                <p class="form-hint">
                  採用綠界文件公開之測試商店（MerchantID 2000132 等），僅供開發測試；正式環境請勿使用。
                </p>
              </div>

              <template v-if="getProviderFields(p.provider_code).length">
                <div v-for="field in getProviderFields(p.provider_code)" :key="field.key" class="form-group provider-cred-field">
                  <label class="form-label form-label--sm">
                    {{ field.label }}
                    <span v-if="field.required" class="required-star">*</span>
                  </label>
                  <div v-if="field.secret" class="credential-input-row has-reveal">
                    <input
                      type="text"
                      :placeholder="field.placeholder ?? ''"
                      :value="providerCredentials[providerKey('payment', p.provider_code)]?.[field.key] ?? ''"
                      class="form-control"
                      :class="{ 'credential-mask': !isSecretVisible(p.provider_code, field.key, 'payment') }"
                      autocomplete="off"
                      :name="`payment-${p.provider_code}-${field.key}`"
                      :readonly="!credentialInputsUnlocked"
                      @focus="credentialInputsUnlocked = true"
                      @input="setCredentialField(p.provider_code, field.key, $event.target.value, 'payment')"
                    />
                    <button
                      type="button"
                      class="btn-credential-reveal"
                      :title="isSecretVisible(p.provider_code, field.key, 'payment') ? '隱藏內容' : '顯示內容'"
                      :aria-pressed="isSecretVisible(p.provider_code, field.key, 'payment')"
                      @click="toggleSecretVisible(p.provider_code, field.key, 'payment')"
                    >
                      <i :class="isSecretVisible(p.provider_code, field.key, 'payment') ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                    </button>
                  </div>
                  <input
                    v-else
                    type="text"
                    :placeholder="field.placeholder ?? ''"
                    :value="providerCredentials[providerKey('payment', p.provider_code)]?.[field.key] ?? ''"
                    class="form-control"
                    autocomplete="off"
                    :name="`payment-${p.provider_code}-${field.key}`"
                    :readonly="!credentialInputsUnlocked"
                    @focus="credentialInputsUnlocked = true"
                    @input="setCredentialField(p.provider_code, field.key, $event.target.value, 'payment')"
                  />
                  <p v-if="field.hint" class="form-hint">{{ field.hint }}</p>
                </div>
              </template>

              <template v-else>
                <div v-for="(val, key) in (providerCredentials[providerKey('payment', p.provider_code)] ?? {})" :key="key" class="credential-kv-row">
                  <input :value="key" class="form-control credential-kv-row__key" readonly />
                  <div class="credential-input-row has-reveal credential-kv-row__value">
                    <input
                      type="text"
                      :value="val"
                      class="form-control"
                      :class="{ 'credential-mask': !isSecretVisible(p.provider_code, key, 'payment') }"
                      autocomplete="off"
                      :name="`payment-${p.provider_code}-${key}`"
                      :readonly="!credentialInputsUnlocked"
                      @focus="credentialInputsUnlocked = true"
                      @input="setCredentialField(p.provider_code, key, $event.target.value, 'payment')"
                    />
                    <button
                      type="button"
                      class="btn-credential-reveal"
                      :title="isSecretVisible(p.provider_code, key, 'payment') ? '隱藏內容' : '顯示內容'"
                      @click="toggleSecretVisible(p.provider_code, key, 'payment')"
                    >
                      <i :class="isSecretVisible(p.provider_code, key, 'payment') ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                    </button>
                  </div>
                  <button type="button" class="btn btn-secondary credential-kv-row__remove" @click="removeCredentialField(p.provider_code, key, 'payment')">
                    <i class="ri-delete-bin-line"></i>
                  </button>
                </div>
                <div class="credential-add-row">
                  <input v-model="newCredentialKey" class="form-control credential-add-row__key" placeholder="欄位名稱" />
                  <button type="button" class="btn btn-secondary" :disabled="!newCredentialKey" @click="addCredentialField(p.provider_code, 'payment')">
                    + 新增欄位
                  </button>
                </div>
              </template>

              <div class="form-group provider-cred-field">
                <label class="form-label form-label--sm">環境模式</label>
                <div class="status-radio-group">
                  <div class="form-check form-check-inline form-radio-secondary">
                    <input
                      :id="`payment-env-sandbox-${p.provider_code}`"
                      v-model="p.is_sandbox"
                      class="form-check-input"
                      type="radio"
                      :value="true"
                    />
                    <label class="form-check-label" :for="`payment-env-sandbox-${p.provider_code}`">測試</label>
                  </div>
                  <div class="form-check form-check-inline form-radio-secondary">
                    <input
                      :id="`payment-env-live-${p.provider_code}`"
                      v-model="p.is_sandbox"
                      class="form-check-input"
                      type="radio"
                      :value="false"
                    />
                    <label class="form-check-label" :for="`payment-env-live-${p.provider_code}`">正式</label>
                  </div>
                </div>
              </div>
              <p class="form-hint">編輯內容需按本區塊右側「儲存」才會寫入。</p>
            </div>
          </div>

          <div class="add-provider-form">
            <div class="add-provider-row">
              <div class="form-group add-provider-field add-provider-field--service">
                <label class="form-label form-label--sm">金流服務</label>
                <select v-model="newProvider.provider_code" class="form-control">
                  <option value="" disabled>請選擇</option>
                  <option v-for="opt in PROVIDER_PRESET_SELECT_OPTIONS" :key="opt.value" :value="opt.value">
                    {{ opt.label }}
                  </option>
                </select>
              </div>
              <div v-if="newProvider.provider_code === '__custom__'" class="form-group add-provider-field add-provider-field--grow">
                <label class="form-label form-label--sm">自訂代碼</label>
                <input
                  v-model="newProviderCustomCode"
                  class="form-control"
                  placeholder="英數小寫，如 other_gateway"
                  autocomplete="off"
                />
              </div>
              <div v-if="newProvider.provider_code === '__custom__'" class="form-group add-provider-field add-provider-field--grow">
                <label class="form-label form-label--sm">顯示名稱</label>
                <input
                  v-model="newProviderDisplayName"
                  class="form-control"
                  maxlength="120"
                  placeholder="選填，未填則只顯示代碼"
                  autocomplete="off"
                />
              </div>
              <div class="form-group add-provider-field add-provider-field--mode">
                <label class="form-label form-label--sm">環境模式</label>
                <div class="status-radio-group">
                  <div class="form-check form-check-inline form-radio-secondary">
                    <input id="new-payment-env-sandbox" v-model="newProvider.is_sandbox" class="form-check-input" type="radio" :value="true" />
                    <label class="form-check-label" for="new-payment-env-sandbox">測試</label>
                  </div>
                  <div class="form-check form-check-inline form-radio-secondary">
                    <input id="new-payment-env-live" v-model="newProvider.is_sandbox" class="form-check-input" type="radio" :value="false" />
                    <label class="form-check-label" for="new-payment-env-live">正式</label>
                  </div>
                </div>
              </div>
              <button
                type="button"
                class="btn btn-secondary add-provider-submit"
                :disabled="!newProviderResolvedCode || commerceSaving"
                @click="handleAddProvider"
              >
                新增
              </button>
            </div>
          </div>
        </div>
        <div v-else class="form-body commerce-order-capture-note">
          <p class="form-hint commerce-order-capture-note__text">
            已開啟僅收訂單，無須設定結算方式與線上金流；關閉「僅收訂單」後可再度調整。
          </p>
        </div>
      </div>

      <!-- 電子發票（與金流同左欄，避免物流展開時被往下推） -->
      <div class="settings-card settings-card--integration">
        <div class="settings-card-header settings-card-header--with-actions">
          <i class="ri-file-list-3-line" style="color: #7c3aed;"></i>
          <div class="settings-card-header__main">
            <h2 class="settings-card-title">電子發票</h2>
            <p class="settings-card-desc">設定結帳載具選項與開立服務商（本期僅益欣自動開立）</p>
          </div>
          <div class="settings-card-header__actions">
            <span v-if="isScopeDirty('integration_invoice')" class="save-feedback save-feedback--dirty">未儲存</span>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="commerceSaving"
              @click="handleScopedSave('integration_invoice')"
            >
              <span v-if="commerceSaving && lastSavedScope === 'integration_invoice'" class="spinner-sm"></span>
              {{ commerceSaving && lastSavedScope === 'integration_invoice' ? '儲存中...' : '儲存' }}
            </button>
          </div>
        </div>
        <div class="form-body">
          <div class="invoice-option-columns">
            <section class="invoice-option-panel">
              <h3 class="invoice-option-title">個人</h3>
              <p class="form-hint">結帳選「個人」時可出現的方式</p>
              <label class="checkbox-line"><input type="checkbox" class="toggle-input" :checked="invoiceCarrierOn('personalCarriers', 'email')" @change="toggleInvoiceCarrier('personalCarriers', 'email')"> E-mail</label>
              <label class="checkbox-line"><input type="checkbox" class="toggle-input" :checked="invoiceCarrierOn('personalCarriers', 'mobile')" @change="toggleInvoiceCarrier('personalCarriers', 'mobile')"> 手機載具</label>
            </section>
            <section class="invoice-option-panel">
              <h3 class="invoice-option-title">公司</h3>
              <p class="form-hint">有勾任一項，結帳才出現「公司」</p>
              <label class="checkbox-line"><input type="checkbox" class="toggle-input" :checked="invoiceCarrierOn('companyCarriers', 'company_email')" @change="toggleInvoiceCarrier('companyCarriers', 'company_email')"> E-mail</label>
              <label class="checkbox-line"><input type="checkbox" class="toggle-input" :checked="invoiceCarrierOn('companyCarriers', 'ship_with_goods')" @change="toggleInvoiceCarrier('companyCarriers', 'ship_with_goods')"> 隨貨寄出</label>
            </section>
          </div>
          <p class="form-hint">紙本發票這一期不開放。勾選與服務商變更需按本區塊右側「儲存」。</p>
          <p v-if="!visibleInvoiceProviders.length" class="commerce-empty-hint">尚無電子發票服務，可新增下方服務。</p>
          <div v-for="p in visibleInvoiceProviders" :key="`invoice-${p.provider_code}`" class="provider-block">
            <div class="provider-row">
              <div class="provider-info">
                <span class="provider-code">{{ integrationProviderRowLabel('invoice', p.provider_code, p.display_name) }}</span>
                <span class="provider-badge" :class="p.is_sandbox ? 'sandbox' : 'live'">
                  {{ p.is_sandbox ? '測試' : '正式' }}
                </span>
              </div>
              <div class="provider-actions">
                <div class="provider-enable-group" title="啟用後，訂單付款完成即自動開立發票">
                  <button
                    type="button"
                    class="provider-switch"
                    :class="{ 'provider-switch--on': p.is_enabled }"
                    role="switch"
                    :aria-checked="p.is_enabled ? 'true' : 'false'"
                    :aria-label="p.is_enabled ? '啟用中，付款完成後自動開立' : '已停用，不會自動開立'"
                    @click="onInvoiceProviderEnabledSwitch(p)"
                  />
                  <span class="provider-enable-text">{{ p.is_enabled ? '啟用' : '停用' }}</span>
                </div>
                <button
                  type="button"
                  class="btn-provider-edit"
                  @click="toggleProviderExpand(p.provider_code, 'invoice')"
                  :title="expandedProvider === providerKey('invoice', p.provider_code) ? '收合' : '展開'"
                >
                  <i :class="expandedProvider === providerKey('invoice', p.provider_code) ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
                  {{ expandedProvider === providerKey('invoice', p.provider_code) ? '收合' : '展開' }}
                </button>
                <button type="button" class="btn-provider-delete" title="刪除此電子發票設定" @click="handleDeleteInvoiceProvider(p)">
                  <i class="ri-delete-bin-line"></i>
                  刪除
                </button>
              </div>
            </div>

            <div v-if="expandedProvider === providerKey('invoice', p.provider_code)" class="provider-credentials">
              <div class="credentials-hint">
                <i class="ri-shield-keyhole-line"></i>
                電子發票憑證與金流憑證相互獨立，請分別填寫。憑證儲存於後端，請勿與他人分享。
              </div>
              <div
                v-if="String(p.provider_code).toLowerCase() === 'ecpay' && p.is_sandbox"
                class="ecpay-sandbox-fill ecpay-sandbox-fill--invoice"
              >
                <button type="button" class="btn btn-secondary" @click="fillEcpayInvoiceSandboxCredentials(p.provider_code)">
                  <i class="ri-flask-line"></i>
                  填入綠界科技測試參數
                </button>
                <p class="form-hint">
                  採用綠界科技測試環境公開之特店編號與 Hash（與金流沙盒的 HashKey／HashIV 不同），僅供開發測試；正式環境請勿使用。測試端點：<code>einvoice-stage.ecpay.com.tw</code>
                </p>
              </div>
              <div v-for="field in getProviderFields(p.provider_code, 'invoice')" :key="field.key" class="form-group provider-cred-field">
                <label class="form-label form-label--sm">
                  {{ field.label }}
                  <span v-if="field.required" class="required-star">*</span>
                </label>
                <div v-if="field.secret" class="credential-input-row has-reveal">
                  <input
                    type="text"
                    :placeholder="field.placeholder ?? ''"
                    :value="providerCredentials[providerKey('invoice', p.provider_code)]?.[field.key] ?? ''"
                    class="form-control"
                    :class="{ 'credential-mask': !isSecretVisible(p.provider_code, field.key, 'invoice') }"
                    autocomplete="off"
                    :name="`invoice-${p.provider_code}-${field.key}`"
                    :readonly="!credentialInputsUnlocked"
                    @focus="credentialInputsUnlocked = true"
                    @input="setCredentialField(p.provider_code, field.key, $event.target.value, 'invoice')"
                  />
                  <button
                    type="button"
                    class="btn-credential-reveal"
                    :title="isSecretVisible(p.provider_code, field.key, 'invoice') ? '隱藏內容' : '顯示內容'"
                    @click="toggleSecretVisible(p.provider_code, field.key, 'invoice')"
                  >
                    <i :class="isSecretVisible(p.provider_code, field.key, 'invoice') ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                  </button>
                </div>
                <input
                  v-else
                  type="text"
                  :placeholder="field.placeholder ?? ''"
                  :value="providerCredentials[providerKey('invoice', p.provider_code)]?.[field.key] ?? ''"
                  class="form-control"
                  autocomplete="off"
                  :name="`invoice-${p.provider_code}-${field.key}`"
                  :readonly="!credentialInputsUnlocked"
                  @focus="credentialInputsUnlocked = true"
                  @input="setCredentialField(p.provider_code, field.key, $event.target.value, 'invoice')"
                />
                <p v-if="field.hint" class="form-hint">{{ field.hint }}</p>
              </div>
              <div class="form-group provider-cred-field">
                <label class="form-label form-label--sm">環境模式</label>
                <div class="status-radio-group">
                  <div class="form-check form-check-inline form-radio-secondary">
                    <input
                      :id="`invoice-env-sandbox-${p.provider_code}`"
                      v-model="p.is_sandbox"
                      class="form-check-input"
                      type="radio"
                      :value="true"
                    />
                    <label class="form-check-label" :for="`invoice-env-sandbox-${p.provider_code}`">測試</label>
                  </div>
                  <div class="form-check form-check-inline form-radio-secondary">
                    <input
                      :id="`invoice-env-live-${p.provider_code}`"
                      v-model="p.is_sandbox"
                      class="form-check-input"
                      type="radio"
                      :value="false"
                    />
                    <label class="form-check-label" :for="`invoice-env-live-${p.provider_code}`">正式</label>
                  </div>
                </div>
                <p class="form-hint">測試環境僅供測試，開立的發票不具法律效力。正式上線前請務必切換至「正式」。啟用此服務後，訂單付款完成即自動開立。編輯內容需按本區塊右側「儲存」才會寫入。</p>
              </div>
            </div>
          </div>

          <div v-if="invoiceProviderOptionsToAdd.length" class="add-provider-form">
            <div class="add-provider-row">
              <div class="form-group add-provider-field add-provider-field--service">
                <label class="form-label form-label--sm">電子發票服務</label>
                <select v-model="newInvoiceProvider.provider_code" class="form-control">
                  <option value="" disabled>請選擇</option>
                  <option v-for="opt in invoiceProviderOptionsToAdd" :key="opt.value" :value="opt.value">
                    {{ opt.label }}
                  </option>
                </select>
              </div>
              <div class="form-group add-provider-field add-provider-field--mode">
                <label class="form-label form-label--sm">環境模式</label>
                <div class="status-radio-group">
                  <div class="form-check form-check-inline form-radio-secondary">
                    <input id="new-invoice-env-sandbox" v-model="newInvoiceProvider.is_sandbox" class="form-check-input" type="radio" :value="true" />
                    <label class="form-check-label" for="new-invoice-env-sandbox">測試</label>
                  </div>
                  <div class="form-check form-check-inline form-radio-secondary">
                    <input id="new-invoice-env-live" v-model="newInvoiceProvider.is_sandbox" class="form-check-input" type="radio" :value="false" />
                    <label class="form-check-label" for="new-invoice-env-live">正式</label>
                  </div>
                </div>
              </div>
              <button
                type="button"
                class="btn btn-secondary add-provider-submit"
                :disabled="!newInvoiceProvider.provider_code || commerceSaving"
                @click="handleAddInvoiceProvider"
              >
                新增
              </button>
            </div>
          </div>
        </div>
      </div>
      </div>

      <div v-if="settingsTab === 'general'" class="settings-card">
        <div class="settings-card-header">
          <i class="ri-archive-line" style="color: #0ea5e9;"></i>
          <div>
            <h2 class="settings-card-title">庫存扣減設定</h2>
            <p class="settings-card-desc">設定一般電商單（非店取）預設扣庫倉庫</p>
          </div>
        </div>
        <div class="form-body">
          <div class="form-group">
            <label class="form-label">電商專用倉庫（非簡易模式生效）</label>
            <p v-if="inventoryModeLoading" class="form-hint">讀取進銷存模式中...</p>
            <p v-else-if="inventoryModeError" class="form-hint form-hint--error">{{ inventoryModeError }}</p>
            <p v-else class="form-hint">
              目前進銷存模式：<strong>{{ inventoryModeLabel }}</strong>
            </p>
            <template v-if="!isSimpleInventoryMode">
              <select v-model="commerceSettings.ecomWarehouseId" class="form-control form-control--narrow">
                <option value="" disabled>請選擇電商專用倉庫</option>
                <option v-for="wh in activeWarehouses" :key="`commerce-ecom-${wh.id}`" :value="wh.id">
                  {{ wh.warehouseName }}
                </option>
              </select>
              <p class="form-hint">
                一般電商單（非店取）會從此倉庫扣庫；進銷存模式下此欄位必填。
              </p>
              <p v-if="ecomWarehouseValidationMessage" class="form-hint form-hint--error">
                {{ ecomWarehouseValidationMessage }}
              </p>
              <p v-if="warehousesLoading" class="form-hint">倉庫清單載入中...</p>
              <p v-else-if="warehouseLoadError" class="form-hint form-hint--error">{{ warehouseLoadError }}</p>
              <p v-else-if="!activeWarehouses.length" class="form-hint">目前尚無可用倉庫，請先建立倉庫。</p>
            </template>
            <p v-else class="form-hint">目前為簡易模式，無需設定電商專用倉庫。</p>
          </div>
        </div>
      </div>

      <div v-if="settingsTab === 'general'" class="settings-card">
        <div class="settings-card-header">
          <i class="ri-coin-line" style="color: #f59e0b;"></i>
          <div>
            <h2 class="settings-card-title">點數折抵</h2>
            <p class="settings-card-desc">設定結帳時點數的折抵規則</p>
          </div>
        </div>
        <div class="form-body">
          <div class="form-group">
            <label class="toggle-row">
              <span>允許點數折抵現金</span>
              <input v-model="commerceSettings.allowPointsCash" type="checkbox" class="toggle-input" />
            </label>
            <p class="form-hint">開啟後，會員可於結帳時使用點數折抵訂單金額。</p>
          </div>
          <div class="form-group">
            <label class="toggle-row">
              <span>限全額點數兌換（LIFF 兌換商城）</span>
              <input v-model="commerceSettings.allowFullPointsOnly" type="checkbox" class="toggle-input" />
            </label>
            <p class="form-hint">開啟後，LIFF 兌換商城的商品只能以全額點數兌換，不允許混合付款。</p>
          </div>
        </div>
      </div>

      <div v-if="settingsTab === 'integration'" class="commerce-integration-grid__col commerce-integration-grid__col--side">
      <div class="settings-card settings-card--integration">
        <div class="settings-card-header settings-card-header--with-actions">
          <i class="ri-truck-line" style="color: #0ea5e9;"></i>
          <div class="settings-card-header__main">
            <h2 class="settings-card-title">物流</h2>
            <p class="settings-card-desc">管理物流服務商憑證與貨態回調（目前支援綠界）</p>
          </div>
          <div class="settings-card-header__actions">
            <span v-if="isScopeDirty('integration_logistics')" class="save-feedback save-feedback--dirty">未儲存</span>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="commerceSaving"
              @click="handleScopedSave('integration_logistics')"
            >
              <span v-if="commerceSaving && lastSavedScope === 'integration_logistics'" class="spinner-sm"></span>
              {{ commerceSaving && lastSavedScope === 'integration_logistics' ? '儲存中...' : '儲存' }}
            </button>
          </div>
        </div>
        <div class="form-body">
          <p v-if="!visibleLogisticsProviders.length" class="commerce-empty-hint">尚無物流 Provider，可新增下方 Provider。</p>
          <div v-for="p in visibleLogisticsProviders" :key="`logistics-${p.provider_code}`" class="provider-block">
            <div class="provider-row">
              <div class="provider-info">
                <span class="provider-code">{{ integrationProviderRowLabel('logistics', p.provider_code, p.display_name) }}</span>
                <span class="provider-badge" :class="p.is_sandbox ? 'sandbox' : 'live'">
                  {{ p.is_sandbox ? '測試' : '正式' }}
                </span>
              </div>
              <div class="provider-actions">
                <div class="provider-enable-group" title="是否啟用此物流">
                  <button
                    type="button"
                    class="provider-switch"
                    :class="{ 'provider-switch--on': p.is_enabled }"
                    role="switch"
                    :aria-checked="p.is_enabled ? 'true' : 'false'"
                    @click="onLogisticsProviderEnabledSwitch(p)"
                  />
                  <span class="provider-enable-text">{{ p.is_enabled ? '啟用' : '停用' }}</span>
                </div>
                <button
                  type="button"
                  class="btn-provider-edit"
                  @click="toggleProviderExpand(p.provider_code, 'logistics')"
                  :title="expandedProvider === providerKey('logistics', p.provider_code) ? '收合' : '展開'"
                >
                  <i :class="expandedProvider === providerKey('logistics', p.provider_code) ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
                  {{ expandedProvider === providerKey('logistics', p.provider_code) ? '收合' : '展開' }}
                </button>
                <button type="button" class="btn-provider-delete" title="刪除此物流設定" @click="handleDeleteLogisticsProvider(p)">
                  <i class="ri-delete-bin-line"></i>
                  刪除
                </button>
              </div>
            </div>

            <div v-if="expandedProvider === providerKey('logistics', p.provider_code)" class="provider-credentials">
              <div class="credentials-hint">
                <i class="ri-shield-keyhole-line"></i>
                物流 callback 與憑證設定會被後端使用於驗簽與狀態同步。
              </div>
              <div v-if="!isCatalogIntegrationProvider('logistics', p.provider_code)" class="form-group provider-cred-field">
                <label class="form-label form-label--sm">顯示名稱</label>
                <input
                  v-model="p.display_name_draft"
                  class="form-control"
                  maxlength="120"
                  placeholder="選填，未填則只顯示代碼"
                  :aria-label="`${p.provider_code} 顯示名稱`"
                />
                <p class="form-hint">與憑證一起儲存。儲存後，上方標題會改成「名稱 (代碼)」。</p>
              </div>
              <div v-if="String(p.provider_code).toLowerCase() === 'ecpay'" class="form-group provider-cred-field">
                <span class="form-label form-label--sm">物流類型</span>
                <div class="status-radio-group" role="radiogroup" aria-label="物流類型">
                  <div class="form-check form-check-inline form-radio-secondary">
                    <input
                      :id="`logistics-mode-b2c-${p.provider_code}`"
                      v-model="p.logistics_mode_draft"
                      class="form-check-input"
                      type="radio"
                      value="b2c"
                    />
                    <label class="form-check-label" :for="`logistics-mode-b2c-${p.provider_code}`">B2C</label>
                  </div>
                  <div class="form-check form-check-inline form-radio-secondary">
                    <input
                      :id="`logistics-mode-c2c-${p.provider_code}`"
                      v-model="p.logistics_mode_draft"
                      class="form-check-input"
                      type="radio"
                      value="c2c"
                    />
                    <label class="form-check-label" :for="`logistics-mode-c2c-${p.provider_code}`">C2C</label>
                  </div>
                </div>
              </div>
              <div
                v-if="String(p.provider_code).toLowerCase() === 'ecpay' && p.is_sandbox && getProviderFields(p.provider_code, 'logistics').length"
                class="ecpay-sandbox-fill"
              >
                <button type="button" class="btn btn-secondary" @click="fillEcpayLogisticsSandboxCredentials(p)">
                  <i class="ri-flask-line"></i>
                  填入綠界物流測試參數
                </button>
              </div>

              <div v-for="field in getProviderFields(p.provider_code, 'logistics')" :key="field.key" class="form-group provider-cred-field">
                <label class="form-label form-label--sm">
                  {{ field.label }}
                  <span v-if="field.required" class="required-star">*</span>
                </label>
                <div v-if="field.secret" class="credential-input-row has-reveal">
                  <input
                    type="text"
                    :value="providerCredentials[providerKey('logistics', p.provider_code)]?.[field.key] ?? ''"
                    class="form-control"
                    :class="{ 'credential-mask': !isSecretVisible(p.provider_code, field.key, 'logistics') }"
                    autocomplete="off"
                    :name="`logistics-${p.provider_code}-${field.key}`"
                    :readonly="!credentialInputsUnlocked"
                    @focus="credentialInputsUnlocked = true"
                    @input="setCredentialField(p.provider_code, field.key, $event.target.value, 'logistics')"
                  />
                  <button
                    type="button"
                    class="btn-credential-reveal"
                    :title="isSecretVisible(p.provider_code, field.key, 'logistics') ? '隱藏內容' : '顯示內容'"
                    @click="toggleSecretVisible(p.provider_code, field.key, 'logistics')"
                  >
                    <i :class="isSecretVisible(p.provider_code, field.key, 'logistics') ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                  </button>
                </div>
                <input
                  v-else
                  type="text"
                  :value="providerCredentials[providerKey('logistics', p.provider_code)]?.[field.key] ?? ''"
                  class="form-control"
                  autocomplete="off"
                  :name="`logistics-${p.provider_code}-${field.key}`"
                  :readonly="!credentialInputsUnlocked"
                  @focus="credentialInputsUnlocked = true"
                  @input="setCredentialField(p.provider_code, field.key, $event.target.value, 'logistics')"
                />
                <p v-if="field.hint" class="form-hint">{{ field.hint }}</p>
              </div>

              <div class="form-group provider-cred-field">
                <label class="form-label form-label--sm">環境模式</label>
                <div class="status-radio-group">
                  <div class="form-check form-check-inline form-radio-secondary">
                    <input
                      :id="`logistics-env-sandbox-${p.provider_code}`"
                      v-model="p.is_sandbox"
                      class="form-check-input"
                      type="radio"
                      :value="true"
                    />
                    <label class="form-check-label" :for="`logistics-env-sandbox-${p.provider_code}`">測試</label>
                  </div>
                  <div class="form-check form-check-inline form-radio-secondary">
                    <input
                      :id="`logistics-env-live-${p.provider_code}`"
                      v-model="p.is_sandbox"
                      class="form-check-input"
                      type="radio"
                      :value="false"
                    />
                    <label class="form-check-label" :for="`logistics-env-live-${p.provider_code}`">正式</label>
                  </div>
                </div>
              </div>
              <p class="form-hint">編輯內容需按本區塊右側「儲存」才會寫入。</p>
            </div>
          </div>

          <div class="add-provider-form">
            <div class="add-provider-row">
              <div class="form-group add-provider-field add-provider-field--service">
                <label class="form-label form-label--sm">物流服務</label>
                <select v-model="newLogisticsProvider.provider_code" class="form-control">
                  <option value="" disabled>請選擇</option>
                  <option v-for="opt in LOGISTICS_PROVIDER_PRESET_SELECT_OPTIONS" :key="opt.value" :value="opt.value">
                    {{ opt.label }}
                  </option>
                </select>
              </div>
              <div v-if="newLogisticsProvider.provider_code === '__custom__'" class="form-group add-provider-field add-provider-field--grow">
                <label class="form-label form-label--sm">自訂代碼</label>
                <input
                  v-model="newLogisticsProviderCustomCode"
                  class="form-control"
                  placeholder="英數小寫，如 home_delivery"
                  autocomplete="off"
                />
              </div>
              <div v-if="newLogisticsProvider.provider_code === '__custom__'" class="form-group add-provider-field add-provider-field--grow">
                <label class="form-label form-label--sm">顯示名稱</label>
                <input
                  v-model="newLogisticsProviderDisplayName"
                  class="form-control"
                  maxlength="120"
                  placeholder="選填，未填則只顯示代碼"
                  autocomplete="off"
                />
              </div>
              <div class="form-group add-provider-field add-provider-field--mode">
                <label class="form-label form-label--sm">環境模式</label>
                <div class="status-radio-group">
                  <div class="form-check form-check-inline form-radio-secondary">
                    <input id="new-logistics-env-sandbox" v-model="newLogisticsProvider.is_sandbox" class="form-check-input" type="radio" :value="true" />
                    <label class="form-check-label" for="new-logistics-env-sandbox">測試</label>
                  </div>
                  <div class="form-check form-check-inline form-radio-secondary">
                    <input id="new-logistics-env-live" v-model="newLogisticsProvider.is_sandbox" class="form-check-input" type="radio" :value="false" />
                    <label class="form-check-label" for="new-logistics-env-live">正式</label>
                  </div>
                </div>
              </div>
              <button
                type="button"
                class="btn btn-secondary add-provider-submit"
                :disabled="!newLogisticsProviderResolvedCode || commerceSaving"
                @click="handleAddLogisticsProvider"
              >
                新增
              </button>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>

      <div
        v-show="settingsTab === 'analytics'"
        class="commerce-settings-stack commerce-settings-tab-pane commerce-settings-stack--glued"
        role="tabpanel"
        aria-label="分析與追蹤"
      >
        <div class="settings-card">
          <div class="settings-card-header settings-card-header--with-actions">
            <i class="ri-bar-chart-box-line" style="color: #10b981;"></i>
            <div class="settings-card-header__main">
              <h2 class="settings-card-title">分析與追蹤</h2>
              <p class="settings-card-desc">設定 Google Tag Manager 與 GA4 整合</p>
            </div>
            <div class="settings-card-header__actions">
              <span v-if="commerceSaveSuccess && lastSavedScope === 'analytics'" class="save-feedback save-feedback--ok">
                <i class="ri-check-line"></i> 已儲存
              </span>
              <span v-if="commerceSaveError && lastSavedScope === 'analytics'" class="save-feedback save-feedback--err">{{ commerceSaveError }}</span>
              <span v-if="isTabDirty('analytics')" class="save-feedback save-feedback--dirty">未儲存</span>
              <button type="button" class="btn btn-primary" :disabled="commerceSaving" @click="handleScopedSave('analytics')">
                <span v-if="commerceSaving && lastSavedScope === 'analytics'" class="spinner-sm"></span>
                {{ commerceSaving && lastSavedScope === 'analytics' ? '儲存中...' : '儲存' }}
              </button>
            </div>
          </div>
          <div class="form-body">
            <div class="form-group">
              <label class="form-label">GTM Container ID</label>
              <input v-model="commerceSettings.gtmContainerId" class="form-control" placeholder="GTM-XXXXXXX" />
              <p class="form-hint">
                填入後商城 SSR 會自動注入 GTM 腳本。可至
                <a href="/docs/templates/gtm-lockline-commerce-starter.json" target="_blank">下載 GTM 啟動範本</a>
                後匯入您的 GTM 帳號。
              </p>
            </div>
            <div class="form-group">
              <label class="form-label">GA4 Measurement ID</label>
              <input v-model="commerceSettings.ga4MeasurementId" class="form-control" placeholder="G-XXXXXXXXXX" />
            </div>
            <div class="form-group">
              <label class="toggle-row toggle-row--inline">
                <input v-model="commerceSettings.analyticsConsentRequired" type="checkbox" class="toggle-input" />
                <span>要求分析同意（Cookie Banner）</span>
              </label>
              <p class="form-hint">啟用後商城 SSR 在取得使用者同意前不會發送分析事件（符合 GDPR/PDPA 要求）。</p>
            </div>
          </div>
        </div>
      </div>

      <div
        v-show="settingsTab === 'monthly_credit' && monthlyCreditTabAvailable"
        class="commerce-settings-tab-pane"
        role="tabpanel"
      >
        <div v-if="tierCreditLoadError" class="alert alert-danger commerce-tier-credit-alert">{{ tierCreditLoadError }}</div>
        <div v-if="memberTiersLoadError" class="alert alert-danger commerce-tier-credit-alert">{{ memberTiersLoadError }}</div>
        <div class="settings-card commerce-monthly-credit-card">
          <div class="settings-card-header">
            <i class="ri-bank-card-2-line" style="color: #0ea5e9;"></i>
            <div>
              <h2 class="settings-card-title">月結額度</h2>
              <p class="settings-card-desc">
                列出所有啟用中的會員等級，請逐欄填寫月信用額度後按「儲存額度設定」一次寫入；額度空白表示不限制。
              </p>
            </div>
          </div>
          <div class="commerce-advanced-body">
            <div v-if="memberTiersLoading" class="commerce-tier-credit-loading">
              <div class="spinner spinner--sm"></div>
              <span>載入會員等級…</span>
            </div>
            <template v-else>
              <p v-if="sortedActiveMemberTiers.length === 0" class="form-hint commerce-tier-credit-no-tiers">
                尚無啟用中的會員等級，請至「會員等級管理」建立並啟用等級後再設定額度。
              </p>
              <table v-else class="simple-table commerce-tier-credit-table">
                <thead>
                  <tr>
                    <th class="commerce-tier-credit-col-tier">會員等級</th>
                    <th class="commerce-tier-credit-col-limit">月信用額度</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="t in sortedActiveMemberTiers" :key="t.id">
                    <td>{{ t.tierName }}（<code class="commerce-tier-code">{{ t.tierCode }}</code>）</td>
                    <td>
                      <input
                        v-model="tierLimitDraft[t.tierCode]"
                        type="number"
                        min="0"
                        step="0.01"
                        class="form-control commerce-tier-credit-limit-input"
                        placeholder="空白＝不限制"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              <p v-if="orphanTierCredits.length" class="form-hint commerce-tier-credit-orphan-hint">
                以下等級目前已停用，仍保留既有額度資料，啟用等級後可在此編輯：
                <span v-for="(r, idx) in orphanTierCredits" :key="r.id">
                  {{ idx ? '、' : '' }}{{ tierNameForCode(r.member_tier) }}（{{
                    r.monthly_credit_limit != null && r.monthly_credit_limit !== '' ? r.monthly_credit_limit : '不限制'
                  }}）
                </span>
              </p>
              <div v-if="sortedActiveMemberTiers.length" class="commerce-tier-credit-actions">
                <span v-if="monthlyCreditSaveOk" class="save-feedback save-feedback--ok commerce-tier-credit-save-ok">
                  <i class="ri-check-line"></i> 已儲存
                </span>
                <button
                  type="button"
                  class="btn btn-primary"
                  :disabled="tierCreditSaving"
                  @click="handleSaveAllTierCredits"
                >
                  <span v-if="tierCreditSaving" class="spinner-sm"></span>
                  {{ tierCreditSaving ? '儲存中...' : '儲存額度設定' }}
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- ===== 外觀與版型 ===== -->
      <div
        v-show="settingsTab === 'appearance'"
        class="commerce-settings-stack commerce-settings-tab-pane commerce-settings-stack--glued"
        role="tabpanel"
        aria-label="外觀與版型"
      >
        <CommerceAppearanceEditor v-model="commerceSettings.storefrontAppearance" :display-name="commerceSettings.storefrontDisplayName" />
      </div>

      <div v-show="settingsTab === 'home'" class="commerce-settings-stack commerce-settings-tab-pane" role="tabpanel">
        <div class="commerce-tab-toolbar">
          <div class="commerce-tab-toolbar__text">
            <h2 class="commerce-tab-toolbar__title">首頁內容</h2>
            <p class="commerce-tab-toolbar__desc">設定商城首頁區塊與公告</p>
          </div>
          <div class="commerce-tab-toolbar__actions">
            <span v-if="commerceSaveSuccess && lastSavedScope === 'home'" class="save-feedback save-feedback--ok">
              <i class="ri-check-line"></i> 已儲存
            </span>
            <span v-if="commerceSaveError && lastSavedScope === 'home'" class="save-feedback save-feedback--err">{{ commerceSaveError }}</span>
            <span v-if="isTabDirty('home')" class="save-feedback save-feedback--dirty">未儲存</span>
            <button type="button" class="btn btn-primary" :disabled="commerceSaving" @click="handleScopedSave('home')">
              <span v-if="commerceSaving && lastSavedScope === 'home'" class="spinner-sm"></span>
              {{ commerceSaving && lastSavedScope === 'home' ? '儲存中...' : '儲存' }}
            </button>
          </div>
        </div>
        <CommerceHomeContentEditor v-model="commerceSettings.homepageConfig" />
      </div>
      <!-- ===== 選單設定 ===== -->
      <div v-show="settingsTab === 'menu'" class="commerce-settings-stack commerce-settings-tab-pane commerce-settings-stack--glued" role="tabpanel">
        <div class="settings-card">
          <div class="settings-card-header settings-card-header--with-actions">
            <i class="ri-menu-line" style="color: #0ea5e9;"></i>
            <div class="settings-card-header__main">
              <h2 class="settings-card-title">前台導覽選單</h2>
              <p class="settings-card-desc">設定前台頂部導覽列的連結項目</p>
            </div>
            <div class="settings-card-header__actions">
              <span v-if="commerceSaveSuccess && lastSavedScope === 'menu'" class="save-feedback save-feedback--ok">
                <i class="ri-check-line"></i> 已儲存
              </span>
              <span v-if="commerceSaveError && lastSavedScope === 'menu'" class="save-feedback save-feedback--err">{{ commerceSaveError }}</span>
              <span v-if="isTabDirty('menu')" class="save-feedback save-feedback--dirty">未儲存</span>
              <button type="button" class="btn btn-primary" :disabled="commerceSaving" @click="handleScopedSave('menu')">
                <span v-if="commerceSaving && lastSavedScope === 'menu'" class="spinner-sm"></span>
                {{ commerceSaving && lastSavedScope === 'menu' ? '儲存中...' : '儲存' }}
              </button>
            </div>
          </div>
          <div class="form-body">
            <p class="form-hint menu-default-hint">
              可點「套用預設選單」快速填入首頁、全部商品、購物車、會員中心（相對路徑）；套用後請按本區塊右側「儲存」寫入資料庫。
            </p>
            <div v-if="!commerceSettings.menuItems.length" class="empty-hint">尚無選單項目，請「套用預設選單」或按下方「新增項目」建立。</div>
            <div v-for="(item, idx) in commerceSettings.menuItems" :key="idx" class="menu-item-row">
              <div class="menu-item-fields">
                <div class="form-group-inline">
                  <label class="form-label-sm">位置</label>
                  <select v-model="item.position" class="form-control form-control-sm"><option value="main">主要導覽</option><option value="top">頂部資訊列</option></select>
                </div><div class="form-group-inline">
                  <label class="form-label-sm">標題</label>
                  <input v-model="item.label" class="form-control form-control-sm" placeholder="例：商品分類" />
                </div>
                <div class="form-group-inline">
                  <label class="form-label-sm">類型</label>
                  <select v-model="item.type" class="form-control form-control-sm">
                    <option value="url">外部 URL</option>
                    <option value="category">商品分類</option>
                    <option value="hall">館別</option>
                    <option value="cms_page">CMS 頁面</option>
                  </select>
                </div>
                <div class="form-group-inline" v-if="item.type === 'url'">
                  <label class="form-label-sm">連結</label>
                  <input v-model="item.href" class="form-control form-control-sm" placeholder="https://..." />
                </div>
                <div class="form-group-inline" v-else>
                  <label class="form-label-sm">目標</label>
                  <CommerceContentTarget v-model="item.ref" :type="item.type" />
                </div>
                <div class="form-group-inline">
                  <label class="toggle-row-sm">
                    <span>開新視窗</span>
                    <input type="checkbox" class="toggle-input" v-model="item.openInNewTab" />
                  </label>
                </div>
              </div>
              <div class="menu-item-actions">
                <button type="button" class="btn-icon" @click="moveMenuItem(idx, -1)" :disabled="idx === 0" title="上移">
                  <i class="ri-arrow-up-s-line"></i>
                </button>
                <button type="button" class="btn-icon" @click="moveMenuItem(idx, 1)" :disabled="idx === commerceSettings.menuItems.length - 1" title="下移">
                  <i class="ri-arrow-down-s-line"></i>
                </button>
                <button type="button" class="btn-icon btn-icon--danger" @click="removeMenuItem(idx)" title="刪除">
                  <i class="ri-delete-bin-line"></i>
                </button>
              </div>
            </div>
            <div class="menu-items-toolbar">
              <button type="button" class="btn btn-secondary" @click="applyDefaultMenuItems">
                <i class="ri-layout-grid-line"></i> 套用預設選單
              </button>
              <button type="button" class="btn btn-secondary" @click="addMenuItem">
                <i class="ri-add-line"></i> 新增項目
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== Footer 設定 ===== -->
      <div v-show="settingsTab === 'footer'" class="commerce-settings-stack commerce-settings-tab-pane commerce-settings-stack--glued" role="tabpanel">
        <div class="settings-card">
          <div class="settings-card-header settings-card-header--with-actions">
            <i class="ri-layout-bottom-line" style="color: #64748b;"></i>
            <div class="settings-card-header__main">
              <h2 class="settings-card-title">Footer 設定</h2>
              <p class="settings-card-desc">設定 Footer 連結欄與版權文字</p>
            </div>
            <div class="settings-card-header__actions">
              <span v-if="commerceSaveSuccess && lastSavedScope === 'footer'" class="save-feedback save-feedback--ok">
                <i class="ri-check-line"></i> 已儲存
              </span>
              <span v-if="commerceSaveError && lastSavedScope === 'footer'" class="save-feedback save-feedback--err">{{ commerceSaveError }}</span>
              <span v-if="isTabDirty('footer')" class="save-feedback save-feedback--dirty">未儲存</span>
              <button type="button" class="btn btn-primary" :disabled="commerceSaving" @click="handleScopedSave('footer')">
                <span v-if="commerceSaving && lastSavedScope === 'footer'" class="spinner-sm"></span>
                {{ commerceSaving && lastSavedScope === 'footer' ? '儲存中...' : '儲存' }}
              </button>
            </div>
          </div>
          <div class="form-body">
            <CommerceFooterContactEditor v-model="commerceSettings.footerConfig" />
            <div class="form-group">
              <label class="form-label">版權文字</label>
              <input v-model="commerceSettings.footerLegalText" class="form-control" placeholder="© 2025 您的品牌名稱 All rights reserved." />
            </div>
            <div class="form-group">
              <label class="form-label">連結欄</label>
              <p class="form-hint footer-default-hint">
                可點「套用預設 Footer」快速填入聯絡我們、關於我們、隱私權政策、服務條款（相對路徑）；套用後請按本區塊右側「儲存」寫入資料庫。
              </p>
              <div v-if="!commerceSettings.footerColumns.length" class="empty-hint">尚無欄位，請「套用預設 Footer」或按「新增欄」。</div>
              <div v-for="(col, ci) in commerceSettings.footerColumns" :key="ci" class="footer-col-block">
                <div class="footer-col-header">
                  <input v-model="col.title" class="form-control form-control-sm footer-col-title" placeholder="欄標題（選填）" />
                  <button type="button" class="btn-icon btn-icon--danger" @click="removeFooterCol(ci)" title="刪除此欄">
                    <i class="ri-delete-bin-line"></i>
                  </button>
                </div>
                <div v-for="(link, li) in col.links" :key="li" class="footer-link-row">
                  <input v-model="link.label" class="form-control form-control-sm" placeholder="連結文字" />
                  <input v-model="link.href" class="form-control form-control-sm" placeholder="https://..." />
                  <label class="toggle-row-sm">
                    <input type="checkbox" class="toggle-input" v-model="link.openInNewTab" />
                    <span>新視窗</span>
                  </label>
                  <button type="button" class="btn-icon btn-icon--danger" @click="removeFooterLink(ci, li)" title="刪除連結">
                    <i class="ri-close-line"></i>
                  </button>
                </div>
                <button type="button" class="btn btn-outline-secondary btn-sm mt-1" @click="addFooterLink(ci)">
                  <i class="ri-add-line"></i> 新增連結
                </button>
              </div>
              <div class="menu-items-toolbar">
                <button type="button" class="btn btn-secondary" @click="applyDefaultFooterColumns">
                  <i class="ri-layout-bottom-line"></i> 套用預設 Footer
                </button>
                <button type="button" class="btn btn-secondary" @click="addFooterCol">
                  <i class="ri-add-line"></i> 新增欄
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== 收貨時段 ===== -->
      <div v-show="settingsTab === 'delivery'" class="commerce-settings-stack commerce-settings-tab-pane commerce-settings-stack--glued" role="tabpanel">
        <div class="settings-card">
          <div class="settings-card-header settings-card-header--with-actions">
            <i class="ri-time-line" style="color: #f59e0b;"></i>
            <div class="settings-card-header__main">
              <h2 class="settings-card-title">收貨時段設定</h2>
              <p class="settings-card-desc">設定可收貨的時段選項；開啟後買家結帳時須選擇時段</p>
            </div>
            <div class="settings-card-header__actions">
              <span v-if="commerceSaveSuccess && lastSavedScope === 'delivery'" class="save-feedback save-feedback--ok">
                <i class="ri-check-line"></i> 已儲存
              </span>
              <span v-if="commerceSaveError && lastSavedScope === 'delivery'" class="save-feedback save-feedback--err">{{ commerceSaveError }}</span>
              <span v-if="isTabDirty('delivery')" class="save-feedback save-feedback--dirty">未儲存</span>
              <button type="button" class="btn btn-primary" :disabled="commerceSaving" @click="handleScopedSave('delivery')">
                <span v-if="commerceSaving && lastSavedScope === 'delivery'" class="spinner-sm"></span>
                {{ commerceSaving && lastSavedScope === 'delivery' ? '儲存中...' : '儲存' }}
              </button>
            </div>
          </div>
          <div class="form-body">
            <div class="form-group">
              <div class="toggle-row toggle-row--switch toggle-row--inline">
                <div class="provider-enable-group">
                  <button
                    type="button"
                    class="provider-switch"
                    :class="{ 'provider-switch--on': commerceSettings.deliveryTimeConfig.enabled }"
                    role="switch"
                    :aria-checked="commerceSettings.deliveryTimeConfig.enabled ? 'true' : 'false'"
                    :aria-label="
                      commerceSettings.deliveryTimeConfig.enabled
                        ? '收貨時段限制已啟用，點擊改為停用'
                        : '收貨時段限制已停用，點擊改為啟用'
                    "
                    @click="commerceSettings.deliveryTimeConfig.enabled = !commerceSettings.deliveryTimeConfig.enabled"
                  />
                  <span class="provider-enable-text">{{
                    commerceSettings.deliveryTimeConfig.enabled ? '啟用' : '停用'
                  }}</span>
                </div>
                <span class="toggle-row-label">啟用收貨時段限制</span>
              </div>
            </div>
            <template v-if="commerceSettings.deliveryTimeConfig.enabled">
              <div class="form-group">
                <label class="form-label">平日時段</label>
                <div v-for="(slot, si) in commerceSettings.deliveryTimeConfig.weekdaySlots" :key="slot.id || si" class="time-slot-row">
                  <input v-model="slot.label" class="form-control form-control-sm" placeholder="時段名稱（例：上午）" />
                  <input v-model="slot.start" class="form-control form-control-sm" type="time" />
                  <span class="slot-sep">~</span>
                  <input v-model="slot.end" class="form-control form-control-sm" type="time" />
                  <button type="button" class="btn-icon btn-icon--danger" @click="removeDeliverySlot('weekday', si)">
                    <i class="ri-delete-bin-line"></i>
                  </button>
                </div>
                <button type="button" class="btn btn-outline-secondary btn-sm mt-1" @click="addDeliverySlot('weekday')">
                  <i class="ri-add-line"></i> 新增平日時段
                </button>
              </div>
              <div class="form-group">
                <label class="form-label">假日時段（週六日）</label>
                <div v-for="(slot, si) in commerceSettings.deliveryTimeConfig.holidaySlots" :key="slot.id || si" class="time-slot-row">
                  <input v-model="slot.label" class="form-control form-control-sm" placeholder="時段名稱（例：上午）" />
                  <input v-model="slot.start" class="form-control form-control-sm" type="time" />
                  <span class="slot-sep">~</span>
                  <input v-model="slot.end" class="form-control form-control-sm" type="time" />
                  <button type="button" class="btn-icon btn-icon--danger" @click="removeDeliverySlot('holiday', si)">
                    <i class="ri-delete-bin-line"></i>
                  </button>
                </div>
                <button type="button" class="btn btn-outline-secondary btn-sm mt-1" @click="addDeliverySlot('holiday')">
                  <i class="ri-add-line"></i> 新增假日時段
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- ===== 指定出貨日 ===== -->
      <div v-show="settingsTab === 'scheduled'" class="commerce-settings-stack commerce-settings-tab-pane commerce-settings-stack--glued" role="tabpanel">
        <div class="settings-card">
          <div class="settings-card-header settings-card-header--with-actions">
            <i class="ri-calendar-check-line" style="color: #10b981;"></i>
            <div class="settings-card-header__main">
              <h2 class="settings-card-title">指定出貨日設定</h2>
              <p class="settings-card-desc">適用於蛋糕、租用等需提前訂購的商品；開啟後買家可選擇指定出貨／取貨日</p>
            </div>
            <div class="settings-card-header__actions">
              <span v-if="commerceSaveSuccess && lastSavedScope === 'scheduled'" class="save-feedback save-feedback--ok">
                <i class="ri-check-line"></i> 已儲存
              </span>
              <span v-if="commerceSaveError && lastSavedScope === 'scheduled'" class="save-feedback save-feedback--err">{{ commerceSaveError }}</span>
              <span v-if="isTabDirty('scheduled')" class="save-feedback save-feedback--dirty">未儲存</span>
              <button type="button" class="btn btn-primary" :disabled="commerceSaving" @click="handleScopedSave('scheduled')">
                <span v-if="commerceSaving && lastSavedScope === 'scheduled'" class="spinner-sm"></span>
                {{ commerceSaving && lastSavedScope === 'scheduled' ? '儲存中...' : '儲存' }}
              </button>
            </div>
          </div>
          <div class="form-body">
            <div class="form-group">
              <div class="toggle-row toggle-row--switch toggle-row--inline">
                <div class="provider-enable-group">
                  <button
                    type="button"
                    class="provider-switch"
                    :class="{ 'provider-switch--on': commerceSettings.scheduledFulfillment.enabled }"
                    role="switch"
                    :aria-checked="commerceSettings.scheduledFulfillment.enabled ? 'true' : 'false'"
                    :aria-label="
                      commerceSettings.scheduledFulfillment.enabled
                        ? '指定出貨日已啟用，點擊改為停用'
                        : '指定出貨日已停用，點擊改為啟用'
                    "
                    @click="commerceSettings.scheduledFulfillment.enabled = !commerceSettings.scheduledFulfillment.enabled"
                  />
                  <span class="provider-enable-text">{{
                    commerceSettings.scheduledFulfillment.enabled ? '啟用' : '停用'
                  }}</span>
                </div>
                <span class="toggle-row-label">啟用指定出貨日</span>
              </div>
            </div>
            <template v-if="commerceSettings.scheduledFulfillment.enabled">
              <div class="form-group">
                <label class="form-label">預設需提前天數</label>
                <div class="lead-days-row">
                  <input v-model.number="commerceSettings.scheduledFulfillment.defaultLeadDays" type="number" min="1" max="365" class="form-control form-control-sm lead-days-input" />
                  <span class="lead-days-unit">天</span>
                </div>
                <p class="form-hint">下單日 + N 天後才可選為出貨日；商品可各自覆寫此值。</p>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
    </div>

    <div
      v-if="paymentProviderDeleteTarget"
      class="commerce-delete-provider-overlay"
      @click.self="cancelDeletePaymentProvider"
    >
      <div class="commerce-delete-provider-dialog" role="dialog" aria-modal="true" aria-labelledby="commerce-delete-provider-title">
        <div class="commerce-delete-provider-header">
          <h3 id="commerce-delete-provider-title">標記刪除金流</h3>
          <button type="button" class="commerce-delete-provider-close" aria-label="關閉" @click="cancelDeletePaymentProvider">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div class="commerce-delete-provider-body">
          <p>將標記刪除「<strong>{{ paymentProviderDeleteTarget.provider_code }}</strong>」，需按金流區塊「儲存」後才會真正刪除。確定嗎？</p>
          <p v-if="paymentProviderDeleteTarget.is_enabled" class="commerce-delete-provider-enabled-note">
            此通路目前為啟用狀態；刪除後結帳將立即無法選用此金流，無須先停用。
          </p>
          <p class="commerce-delete-provider-warning">憑證將一併移除且無法還原。</p>
        </div>
        <div class="commerce-delete-provider-footer">
          <button type="button" class="btn btn-secondary" :disabled="paymentProviderDeleting" @click="cancelDeletePaymentProvider">
            取消
          </button>
          <button type="button" class="btn btn-danger" :disabled="paymentProviderDeleting" @click="confirmDeletePaymentProvider">
            標記刪除
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { usePageTitle } from '@/composables/usePageTitle.js'
import {
  useCommerceMarketplaceAdmin,
  getBankTransferSaveValidationMessage,
  COMMERCE_SETTINGS_SCOPE_KEYS,
} from '@/composables/useCommerceMarketplaceAdmin.js'
import { useCommerceAdvancedRules } from '@/composables/useCommerceAdvancedRules.js'
import { memberTierService } from '@/api/modules/memberTiers.js'
import { createDefaultCommerceMenuItems } from '@/constants/defaultCommerceMenuItems.js'
import { createDefaultCommerceFooterColumns } from '@/constants/defaultCommerceFooterColumns.js'
import { showConfirm } from '@/utils/swal.js'
import CommerceContentTarget from '@/components/commerce/CommerceContentTarget.vue'
import CommerceHomeContentEditor from '@/components/commerce/CommerceHomeContentEditor.vue'
import CommerceFooterContactEditor from '@/components/commerce/CommerceFooterContactEditor.vue'
import CommerceAppearanceEditor from '@/components/commerce/CommerceAppearanceEditor.vue'
import { warehouseService } from '@/api/modules/warehouses.js'
import { clientSettingsService, NAMESPACES } from '@/api/modules/clientSettings.js'
import FormTipBox from '@/components/form/FormTipBox.vue'
import ImageUploader from '@/components/ImageUploader.vue'
import apiClient from '@/api/axios.js'

usePageTitle('商城設定')

async function uploadCommerceBrandImage(file) {
  const fd = new FormData()
  fd.append('file', file)
  return apiClient.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
}

const settingsTab = ref('general')
const lastSavedScope = ref('')

const credentialInputsUnlocked = ref(false)

const {
  loadError: tierCreditLoadError,
  saving: tierCreditSaving,
  tierCredits,
  reloadTierCredits,
  saveAllTierCredits,
} = useCommerceAdvancedRules()

/** 全部等級（解析顯示名稱）；表單僅編輯啟用中等級 */
const memberTierAll = ref([])
const memberTiersLoading = ref(false)
const memberTiersLoadError = ref('')
const tierLimitDraft = ref({})
const monthlyCreditSaveOk = ref(false)
const activeWarehouses = ref([])
const warehousesLoading = ref(false)
const warehouseLoadError = ref('')
const inventoryModeLoading = ref(false)
const inventoryModeError = ref('')
const inventoryDeductMode = ref('disabled')

const isSimpleInventoryMode = computed(() => inventoryDeductMode.value === 'disabled')
const inventoryModeLabel = computed(() => {
  const map = {
    disabled: '簡易模式',
    specific_warehouse: '進銷存模式（指定倉庫）',
    store_warehouse: '進銷存模式（依門市倉庫扣減)',
  }
  return map[inventoryDeductMode.value] || inventoryDeductMode.value
})

const activeMemberTiers = computed(() => memberTierAll.value.filter((t) => t.isActive !== false))

const sortedActiveMemberTiers = computed(() =>
  [...activeMemberTiers.value].sort((a, b) => (Number(a.displayOrder) || 0) - (Number(b.displayOrder) || 0))
)

const orphanTierCredits = computed(() => {
  const activeCodes = new Set(sortedActiveMemberTiers.value.map((t) => String(t.tierCode)))
  return tierCredits.value.filter((r) => !activeCodes.has(String(r.member_tier)))
})

function tierNameForCode(code) {
  const c = code == null ? '' : String(code)
  const t = memberTierAll.value.find((x) => x.tierCode === c)
  return t ? t.tierName : c
}

function syncTierCreditDraft() {
  const next = {}
  for (const t of sortedActiveMemberTiers.value) {
    const row = tierCredits.value.find((r) => String(r.member_tier) === String(t.tierCode))
    const v = row?.monthly_credit_limit
    next[t.tierCode] = v !== null && v !== undefined && v !== '' ? v : ''
  }
  tierLimitDraft.value = next
}

async function handleSaveAllTierCredits() {
  monthlyCreditSaveOk.value = false
  const codes = sortedActiveMemberTiers.value.map((t) => t.tierCode)
  await saveAllTierCredits(tierLimitDraft.value, codes)
  if (!tierCreditLoadError.value) {
    syncTierCreditDraft()
    monthlyCreditSaveOk.value = true
    window.setTimeout(() => {
      monthlyCreditSaveOk.value = false
    }, 2500)
  }
}

async function loadMemberTiersForCredit() {
  memberTiersLoadError.value = ''
  memberTiersLoading.value = true
  try {
    const res = await memberTierService.getTiers({})
    const list = Array.isArray(res?.data) ? res.data : []
    if (res?.success === false) {
      memberTiersLoadError.value = res?.message || '載入會員等級失敗'
      memberTierAll.value = []
      return
    }
    memberTierAll.value = list
  } catch (e) {
    memberTierAll.value = []
    memberTiersLoadError.value = e?.response?.data?.message || e.message || '載入會員等級失敗'
  } finally {
    memberTiersLoading.value = false
  }
}

async function loadWarehouses() {
  warehousesLoading.value = true
  warehouseLoadError.value = ''
  try {
    const res = await warehouseService.getWarehouses({ status: 'active', pageSize: 200 })
    const items = Array.isArray(res?.data?.items) ? res.data.items : []
    activeWarehouses.value = items
  } catch (e) {
    activeWarehouses.value = []
    warehouseLoadError.value = e?.response?.data?.message || e.message || '載入倉庫清單失敗'
  } finally {
    warehousesLoading.value = false
  }
}

async function loadInventoryMode() {
  inventoryModeLoading.value = true
  inventoryModeError.value = ''
  try {
    const res = await clientSettingsService.get(NAMESPACES.INVENTORY)
    const settings = res?.data?.settings ?? {}
    const mode = String(settings.order_deduct_mode || 'disabled').trim()
    inventoryDeductMode.value = mode === 'all_warehouses'
      ? (settings.default_warehouse_id ? 'specific_warehouse' : 'store_warehouse')
      : mode
  } catch (e) {
    inventoryDeductMode.value = 'disabled'
    inventoryModeError.value = e?.response?.data?.message || e.message || '讀取進銷存設定失敗'
  } finally {
    inventoryModeLoading.value = false
  }
}

const {
  SETTLEMENT_MODE_OPTIONS,
  PROVIDER_PRESET_SELECT_OPTIONS,
  LOGISTICS_PROVIDER_PRESET_SELECT_OPTIONS,
  commerceLoading,
  commerceSaving,
  commerceSaveSuccess,
  commerceSaveError,
  commerceSettings,
  lineOaOptions,
  storefrontFrontUrl,
  suggestedDefaultStorefrontUrl,
  applySuggestedStorefrontUrl,
  paymentProviders,
  invoiceProviders,
  logisticsProviders,
  newProvider,
  newProviderCustomCode,
  newProviderDisplayName,
  newProviderResolvedCode,
  newLogisticsProvider,
  newLogisticsProviderCustomCode,
  newLogisticsProviderDisplayName,
  newLogisticsProviderResolvedCode,
  newInvoiceProvider,
  invoiceProviderOptionsToAdd,
  isCatalogIntegrationProvider,
  integrationProviderRowLabel,
  loadCommerceSettings,
  handleCommerceSave: handleCommerceSaveRaw,
  isScopeDirty,
  isTabDirty,
  revertTab,
  saveIntegrationSection,
  invoiceCarrierOn,
  toggleInvoiceCarrier,
  toggleOrderCaptureOnly,
  onProviderEnabledSwitch,
  handleAddProvider,
  handleDeleteProvider,
  paymentProviderDeleteTarget,
  paymentProviderDeleting,
  cancelDeletePaymentProvider,
  confirmDeletePaymentProvider,
  expandedProvider,
  providerKey,
  toggleProviderExpand,
  providerCredentials,
  providerCredSaving,
  providerCredSuccess,
  providerCredError,
  getProviderFields,
  setCredentialField,
  fillEcpaySandboxCredentials,
  fillEcpayLogisticsSandboxCredentials,
  fillEcpayInvoiceSandboxCredentials,
  onLogisticsProviderEnabledSwitch,
  handleAddLogisticsProvider,
  handleDeleteLogisticsProvider,
  onInvoiceProviderEnabledSwitch,
  handleAddInvoiceProvider,
  handleDeleteInvoiceProvider,
  isSecretVisible,
  toggleSecretVisible,
  newCredentialKey,
  addCredentialField,
  removeCredentialField,
} = useCommerceMarketplaceAdmin()


const visiblePaymentProviders = computed(() =>
  paymentProviders.value.filter((p) => !p._draftDeleted)
)
const visibleInvoiceProviders = computed(() =>
  invoiceProviders.value.filter((p) => !p._draftDeleted)
)
const visibleLogisticsProviders = computed(() =>
  logisticsProviders.value.filter((p) => !p._draftDeleted)
)

/** 月結額度僅 B2B 企業客戶模式使用 */
const monthlyCreditTabAvailable = computed(() => commerceSettings.value.commerceMode === 'b2b')

/** 結算方式已勾選月結帳單（用於顯示額度／B2B 價格提醒） */
const monthlyStatementSelected = computed(() => {
  const modes = commerceSettings.value.settlementModes
  return Array.isArray(modes) && modes.includes('monthly_statement')
})

/** 與前台 checkout SETTINGS_KV_LABELS 鍵名一致（bank_name、account_name 等） */
const BANK_TRANSFER_FIELD_DEFS = [
  { key: 'bank_name', label: '銀行名稱', placeholder: '例：台灣銀行', required: true },
  { key: 'branch', label: '分行（選填）', placeholder: '例：營業部', required: false },
  { key: 'account_name', label: '戶名', placeholder: '例：○○有限公司', required: true },
  { key: 'account_number', label: '帳號', placeholder: '請填寫完整帳號', required: true },
  {
    key: 'notes',
    label: '匯款說明（選填）',
    placeholder: '例：請於匯款後聯繫客服，或於匯款備註填寫訂單編號',
    multiline: true,
    required: false,
  },
]

const bankTransferSelected = computed(() => {
  const modes = commerceSettings.value.settlementModes
  return Array.isArray(modes) && modes.includes('bank_transfer')
})

const bankTransferValidationMessage = computed(() =>
  getBankTransferSaveValidationMessage(
    commerceSettings.value.settlementModes,
    commerceSettings.value.bankTransfer
  )
)

const ecomWarehouseValidationMessage = computed(() => {
  if (inventoryModeLoading.value || inventoryModeError.value || isSimpleInventoryMode.value) return ''
  return String(commerceSettings.value.ecomWarehouseId || '').trim()
    ? ''
    : '目前為進銷存模式，請設定電商專用倉庫'
})

const saveValidationMessage = computed(() => {
  if (bankTransferValidationMessage.value) return bankTransferValidationMessage.value
  if (ecomWarehouseValidationMessage.value) return ecomWarehouseValidationMessage.value
  return ''
})

function isBankTransferRequiredFieldEmpty(field) {
  if (!field?.required || !bankTransferSelected.value) return false
  const v = commerceSettings.value.bankTransfer?.[field.key]
  return !String(v ?? '').trim()
}

function ensureBankTransferObject() {
  const cur = commerceSettings.value.bankTransfer
  if (!cur || typeof cur !== 'object' || Array.isArray(cur)) {
    commerceSettings.value.bankTransfer = {}
  }
}

watch(
  bankTransferSelected,
  (on) => {
    if (on) ensureBankTransferObject()
  },
  { immediate: true }
)

async function requestSettingsTab(nextTab) {
  if (nextTab === settingsTab.value) return
  const leaving = settingsTab.value
  if (isTabDirty(leaving)) {
    const ok = await showConfirm(
      '尚未儲存',
      '目前分頁有未儲存的變更，確定要離開並放棄這些變更嗎？',
      '放棄並離開',
      '繼續編輯'
    )
    if (!ok) return
    revertTab(leaving)
  }
  settingsTab.value = nextTab
}

async function openMonthlyCreditTab() {
  if (!monthlyCreditTabAvailable.value) return
  await requestSettingsTab('monthly_credit')
  if (settingsTab.value !== 'monthly_credit') return
  await Promise.all([reloadTierCredits(), loadMemberTiersForCredit()])
  syncTierCreditDraft()
}

watch(
  () => commerceSettings.value.commerceMode,
  (mode) => {
    if (mode !== 'b2b' && settingsTab.value === 'monthly_credit') {
      settingsTab.value = 'general'
    }
    if (mode === 'b2c') {
      const arr = commerceSettings.value.settlementModes
      if (Array.isArray(arr) && arr.includes('monthly_statement')) {
        const next = arr.filter((s) => s !== 'monthly_statement')
        commerceSettings.value.settlementModes = next.length ? next : ['online_gateway']
      }
    }
  }
)

onMounted(() => {
  loadCommerceSettings()
  loadWarehouses()
  loadInventoryMode()
})

async function applyDefaultMenuItems() {
  const existing = commerceSettings.value.menuItems
  if (Array.isArray(existing) && existing.length > 0) {
    const ok = await showConfirm(
      '套用預設選單',
      '將以預設項目（首頁、全部商品、購物車、會員中心）取代目前所有選單列，確定嗎？',
      '取代並套用',
      '取消'
    )
    if (!ok) return
  }
  commerceSettings.value.menuItems = createDefaultCommerceMenuItems()
}

function addMenuItem() {
  if (!Array.isArray(commerceSettings.value.menuItems)) commerceSettings.value.menuItems = []
  commerceSettings.value.menuItems.push({ label: '', type: 'url', href: '', position: 'main', openInNewTab: false })
}
function removeMenuItem(idx) { commerceSettings.value.menuItems.splice(idx, 1) }
function moveMenuItem(idx, dir) {
  const arr = commerceSettings.value.menuItems
  const target = idx + dir
  if (target < 0 || target >= arr.length) return
  const tmp = arr[idx]; arr[idx] = arr[target]; arr[target] = tmp
}

async function applyDefaultFooterColumns() {
  const cols = commerceSettings.value.footerColumns
  if (Array.isArray(cols) && cols.length > 0) {
    const ok = await showConfirm(
      '套用預設 Footer',
      '將以預設連結欄（聯絡我們、關於我們、隱私權政策、服務條款）取代目前所有Footer 欄位，確定嗎？',
      '取代並套用',
      '取消'
    )
    if (!ok) return
  }
  commerceSettings.value.footerColumns = createDefaultCommerceFooterColumns()
}

function addFooterCol() {
  if (!Array.isArray(commerceSettings.value.footerColumns)) commerceSettings.value.footerColumns = []
  commerceSettings.value.footerColumns.push({ title: '', links: [] })
}
function removeFooterCol(ci) { commerceSettings.value.footerColumns.splice(ci, 1) }
function addFooterLink(ci) { commerceSettings.value.footerColumns[ci].links.push({ label: '', href: '', openInNewTab: false }) }
function removeFooterLink(ci, li) { commerceSettings.value.footerColumns[ci].links.splice(li, 1) }

function addDeliverySlot(type) {
  const cfg = commerceSettings.value.deliveryTimeConfig
  const arr = type === 'weekday' ? cfg.weekdaySlots : cfg.holidaySlots
  arr.push({ id: `slot-${Date.now()}`, label: '', start: '09:00', end: '12:00' })
}
function removeDeliverySlot(type, idx) {
  const cfg = commerceSettings.value.deliveryTimeConfig
  const arr = type === 'weekday' ? cfg.weekdaySlots : cfg.holidaySlots
  arr.splice(idx, 1)
}

async function handleScopedSave(scope) {
  lastSavedScope.value = scope
  if (scope === 'integration_payment') {
    await saveIntegrationSection('payment')
    return
  }
  if (scope === 'integration_invoice') {
    await saveIntegrationSection('invoice')
    return
  }
  if (scope === 'integration_logistics') {
    await saveIntegrationSection('logistics')
    return
  }
  const keys = COMMERCE_SETTINGS_SCOPE_KEYS[scope]
  if (Array.isArray(keys) && keys.length === 0) {
    return
  }
  if (scope === 'general' && ecomWarehouseValidationMessage.value) {
    commerceSaveError.value = ecomWarehouseValidationMessage.value
    return
  }
  await handleCommerceSaveRaw({ scope })
}
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.page-outer {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.page-header {
  background: #f5f7fa;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.header-left { display: flex; align-items: center; }
.header-actions { display: flex; align-items: center; gap: 8px; }

.storefront-default-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: stretch;
}
.storefront-default-row__readonly {
  flex: 1;
  min-width: 200px;
  background: #f8f9fa;
}

:deep(.commerce-storefront-tip-list) {
  margin: 10px 0 0;
  padding-left: 1.35rem;
  font-size: 12px;
  line-height: 1.6;
  color: #4b5563;
}
:deep(.commerce-storefront-tip-list li) {
  margin-top: 8px;
}
:deep(.commerce-storefront-tip-list li:first-child) {
  margin-top: 0;
}

.commerce-line-oa-warn {
  margin: 0 0 16px;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid #fcd34d;
  border-left: 4px solid #d97706;
  background: #fffbeb;
  font-size: 13px;
  line-height: 1.55;
  color: #92400e;
}

.commerce-line-oa-auto-hint {
  margin: 0 0 16px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #166534;
  font-size: 13px;
  line-height: 1.5;
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.page-container {
  padding: 20px;
  padding-top: 72px;
}

.btn { padding: 6px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; border: none; display: inline-flex; align-items: center; gap: 6px; }
.btn-primary { background: #3b82f6; color: #fff; }
.btn-primary:hover:not(:disabled) { background: #2563eb; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

.save-feedback {
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.save-feedback--ok { color: #059669; }
.save-feedback--err { color: #dc2626; }

/* 與 client 系統參數頁一致：settings-card 定義在 settings/Index.vue 的 scoped 內，此頁需自帶卡片樣式 */
.commerce-settings-layout {
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
  container-type: inline-size;
  container-name: commerce-settings;
}

.commerce-settings-tabs-nav {
  display: flex;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px 12px 0 0;
  border-bottom: none;
  overflow-x: auto;
  margin-bottom: 0;
}

.commerce-settings-tab-btn {
  flex: 1;
  min-width: 0;
  padding: 14px 20px;
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, border-color 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
}

.commerce-settings-tab-btn:hover {
  background: #f9fafb;
  color: #374151;
}

.commerce-settings-tab-btn.active {
  color: #667eea;
  border-bottom-color: #667eea;
  background: #f8f9ff;
}

.commerce-settings-tab-btn i {
  font-size: 18px;
}

.commerce-settings-tab-pane {
  padding: 0;
}

.commerce-settings-stack {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 100%;
}

/* 僅「貼齊 Tab」的分頁去頂邊／圓角；整合 Tab 三卡保持獨立區塊感 */
.commerce-settings-layout > .commerce-settings-stack--glued > .settings-card:first-of-type {
  border-top: none;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}

.commerce-settings-layout > .commerce-settings-stack--glued > .commerce-tab-toolbar + .settings-card {
  margin-top: -20px;
  border-top: none;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}

.commerce-settings-layout > .commerce-settings-stack:not(.commerce-settings-stack--glued) {
  padding-top: 16px;
}

/* 左欄：金流＋電子發票；右欄：物流。兩欄高度獨立，展開一側不會把另一側往下推。
   ≤960px 改直排：金流→電子發票→物流。 */
.commerce-settings-stack.commerce-integration-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  align-items: start;
}

.commerce-integration-grid__col {
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
}

.commerce-integration-grid__col > .settings-card {
  margin-bottom: 0;
}

@container commerce-settings (max-width: 960px) {
  .commerce-settings-stack.commerce-integration-grid {
    grid-template-columns: 1fr;
  }

  .commerce-integration-grid__col--side {
    order: 1;
  }

  .commerce-integration-grid__col--main {
    order: 0;
  }
}

@media (max-width: 960px) {
  .commerce-settings-stack.commerce-integration-grid {
    grid-template-columns: 1fr;
  }
}

.commerce-tab-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 16px 20px;
  background: #fff;
  border: 1px solid #e6e8f0;
  border-radius: 9px;
}

.commerce-settings-stack--glued > .commerce-tab-toolbar {
  border-bottom: none;
  border-radius: 9px 9px 0 0;
}

.commerce-tab-toolbar__title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
}

.commerce-tab-toolbar__desc {
  margin: 4px 0 0;
  font-size: 13px;
  color: #6c757d;
}

.commerce-tab-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-left: auto;
}

.settings-card-header--with-actions {
  align-items: center;
}

.settings-card-header__main {
  flex: 1;
  min-width: 0;
}

.settings-card-header__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  flex-shrink: 0;
  margin-left: auto;
}

.save-feedback--dirty {
  color: #b45309;
}

.commerce-monthly-credit-card {
  border-top: none;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}

.commerce-tier-credit-alert {
  margin: 0 0 16px;
}

.commerce-tier-credit-no-tiers {
  margin: 0 0 12px;
  color: #92400e;
}

.commerce-tier-credit-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 0;
  color: #6b7280;
  font-size: 14px;
}

.commerce-tier-credit-loading .spinner--sm {
  width: 22px;
  height: 22px;
  border-width: 2px;
}

.commerce-tier-credit-col-tier {
  min-width: 200px;
}

.commerce-tier-credit-col-limit {
  width: 240px;
}

.commerce-tier-credit-limit-input {
  max-width: 220px;
}

.commerce-tier-code {
  font-size: 12px;
  font-family: ui-monospace, monospace;
}

.commerce-tier-credit-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  flex-wrap: wrap;
}

.commerce-tier-credit-orphan-hint {
  margin-top: 12px;
  color: #6b7280;
  font-size: 13px;
}

.commerce-tier-credit-save-ok {
  margin: 0;
}

.commerce-advanced-body {
  padding: 0 20px 20px;
}

.simple-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  margin-bottom: 12px;
}

.simple-table th,
.simple-table td {
  border: 1px solid #e5e7eb;
  padding: 8px 10px;
  text-align: left;
}

.simple-table th {
  background: #f9fafb;
}

.inline-form {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.btn-sm {
  font-size: 12px;
  padding: 2px 6px;
}

.btn-link {
  background: none;
  border: none;
  color: #3b82f6;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
}

.btn-link:hover {
  color: #2563eb;
}

.settings-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  overflow: hidden;
}

.invoice-option-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 12px;
}

.invoice-option-panel {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 16px;
}

.invoice-option-title {
  margin: 0 0 4px;
  font-size: 15px;
}

.checkbox-line {
  display: flex;
  gap: 8px;
  align-items: center;
  margin: 8px 0;
}

@media (max-width: 700px) {
  .invoice-option-columns {
    grid-template-columns: 1fr;
  }
}

.settings-card-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 24px;
  background: #f8f9ff;
  border-bottom: 1px solid #e9ecef;
}

.settings-card-header > i {
  font-size: 32px;
  margin-top: 2px;
  flex-shrink: 0;
}

.settings-card-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 4px;
  color: #2d3748;
}

.settings-card-desc {
  font-size: 13px;
  color: #6c757d;
  margin: 0;
}


.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 60px 20px;
  color: #6c757d;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e9ecef;
  border-top-color: #667eea;
  border-radius: 50%;
  animation: commerce-settings-spin 0.7s linear infinite;
}

.spinner-sm {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: commerce-settings-spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes commerce-settings-spin {
  to {
    transform: rotate(360deg);
  }
}

.form-body {
  padding: 16px 24px 16px;
}

.commerce-payment-section {
  padding: 4px 24px 20px;
  border-top: 1px solid #f0f0f0;
}

.form-body > .commerce-subsection-title:first-child {
  margin-top: 0;
}

.commerce-subsection-title {
  margin: 0 0 12px;
  font-size: 15px;
  font-weight: 600;
  color: #2d3748;
}

.commerce-empty-hint {
  margin: 0 0 12px;
  font-size: 13px;
  color: #9ca3af;
}

.commerce-order-capture-note {
  padding: 0 24px 20px;
  border-top: 1px solid #f0f0f0;
}

.commerce-order-capture-note__text {
  margin: 0;
}

.form-hint--error {
  color: #ef4444;
}

.form-label--sm {
  font-size: 12px;
}

.required-star {
  color: #ef4444;
}

.add-provider-form {
  --add-provider-control-height: 38px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px dashed #e5e7eb;
}

.add-provider-field {
  margin-bottom: 0;
}

.add-provider-field .form-control {
  height: var(--add-provider-control-height);
}

.add-provider-field--grow {
  flex: 1 1 200px;
  min-width: 160px;
}

.add-provider-field--service {
  flex: 0 0 auto;
  width: 220px;
  max-width: 100%;
}

.add-provider-field--mode {
  flex: 0 0 auto;
  margin-left: 8px;
}

/* 控制項統一高度，讓服務與環境模式的標籤及內容分別對齊。 */
.add-provider-field--mode .status-radio-group {
  height: var(--add-provider-control-height);
  align-items: center;
}

.add-provider-field--mode .form-check {
  margin-bottom: 0;
  min-height: 0;
  padding-left: 0;
}

.add-provider-submit {
  align-self: flex-end;
  margin-bottom: 3px;
}

.provider-cred-field {
  margin-bottom: 10px;
}

.credential-kv-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
  align-items: stretch;
}

.credential-kv-row__key {
  flex: 0 0 140px;
  min-width: 100px;
}

.credential-kv-row__value {
  flex: 1 1 200px;
  min-width: 0;
}

.credential-kv-row__remove {
  flex-shrink: 0;
  align-self: stretch;
}

.credential-add-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
  align-items: center;
}

.credential-add-row__key {
  flex: 0 0 140px;
  min-width: 100px;
}

.form-control--narrow {
  max-width: 200px;
}

.provider-cred-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  align-items: center;
}

.provider-cred-feedback {
  font-size: 13px;
}

.provider-cred-feedback--ok {
  color: #10b981;
}

.provider-cred-feedback--err {
  color: #ef4444;
}

.toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  cursor: pointer;
  font-size: 14px;
  color: #374151;
}

.toggle-row--switch {
  cursor: default;
}

.toggle-row--inline {
  justify-content: flex-start;
  width: fit-content;
  gap: 8px;
}

.toggle-row-label {
  flex: 1;
  min-width: 0;
}

.toggle-input {
  width: 18px;
  height: 18px;
  accent-color: #667eea;
  flex-shrink: 0;
  cursor: pointer;
}

.provider-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 8px 12px;
  padding: 8px 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  margin-bottom: 8px;
}

.provider-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 1 auto;
  min-width: 0;
}

.provider-code {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.provider-badge {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 500;
}

/* 金流／發票／物流區塊內容字級（選項 B：適中放大；標題／按鈕／表單控制項不變） */
.settings-card--integration .provider-code {
  font-size: 15px;
}

.settings-card--integration .provider-badge {
  font-size: 12px;
}

.settings-card--integration .provider-enable-text {
  font-size: 14px;
}

.settings-card--integration .form-hint,
.settings-card--integration .commerce-empty-hint,
.settings-card--integration .credentials-hint,
.settings-card--integration .invoice-credentials-hint {
  font-size: 14px;
}

.settings-card--integration .form-label--sm {
  font-size: 13px;
}

.settings-card--integration .invoice-option-title {
  font-size: 16px;
}

.settings-card--integration .checkbox-line {
  font-size: 14px;
}

.provider-badge.sandbox {
  background: #fef3c7;
  color: #d97706;
}

.provider-badge.live {
  background: #d1fae5;
  color: #059669;
}

.provider-actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 8px;
  margin-left: 0;
}

.provider-enable-group {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.provider-switch {
  position: relative;
  width: 40px;
  height: 22px;
  border-radius: 11px;
  border: none;
  background: #d1d5db;
  cursor: pointer;
  transition: background 0.2s;
  padding: 0;
  flex-shrink: 0;
}

.provider-switch:focus-visible {
  outline: 2px solid #667eea;
  outline-offset: 2px;
}

.provider-switch--on {
  background: #667eea;
}

.provider-switch::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
  transition: transform 0.2s ease;
}

.provider-switch--on::after {
  transform: translateX(18px);
}

.provider-enable-text {
  font-size: 13px;
  font-weight: 500;
  color: #4b5563;
  min-width: 2em;
  user-select: none;
}

.provider-block {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 10px;
  overflow: hidden;
}

.provider-block .provider-row {
  border: none;
  border-radius: 0;
  margin-bottom: 0;
}

.btn-provider-edit {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.3;
  padding: 6px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  color: #374151;
  cursor: pointer;
  transition: background 0.15s;
  flex-shrink: 0;
}

.btn-provider-edit i {
  font-size: 16px;
}

.btn-provider-edit:hover {
  background: #f3f4f6;
}

.btn-provider-delete {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.3;
  padding: 6px 12px;
  border: 1px solid #fecaca;
  border-radius: 6px;
  background: #fff;
  color: #b91c1c;
  cursor: pointer;
  transition: background 0.15s;
  flex-shrink: 0;
}

.btn-provider-delete i {
  font-size: 16px;
}

.btn-provider-delete:hover {
  background: #fef2f2;
}

.provider-credentials {
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
  padding: 16px;
}

.credentials-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #6b7280;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 6px;
  padding: 6px 10px;
  margin-bottom: 14px;
}

.invoice-credentials-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #6b7280;
  background: #f5f3ff;
  border: 1px solid #ddd6fe;
  border-radius: 6px;
  padding: 6px 10px;
  margin-bottom: 14px;
}

.commerce-monthly-statement-tip {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin-top: 14px;
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid #c7d2fe;
  background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
  box-shadow: 0 1px 3px rgba(79, 70, 229, 0.12);
}

.commerce-monthly-statement-tip__icon {
  flex-shrink: 0;
  font-size: 22px;
  color: #4f46e5;
  margin-top: 2px;
}

.commerce-monthly-statement-tip__body {
  min-width: 0;
  flex: 1;
}

.commerce-monthly-statement-tip__title {
  margin: 0 0 10px;
  font-size: 15px;
  font-weight: 600;
  color: #312e81;
}

.commerce-monthly-statement-tip__list {
  margin: 0;
  padding-left: 1.15rem;
  color: #1e1b4b;
  font-size: 13px;
  line-height: 1.55;
}

.commerce-monthly-statement-tip__list li + li {
  margin-top: 8px;
}

.commerce-monthly-statement-tip__link {
  font-weight: 600;
  color: #4338ca;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.commerce-monthly-statement-tip__link:hover {
  color: #3730a3;
}

.commerce-monthly-statement-tip__actions {
  margin: 12px 0 0;
}

.commerce-monthly-statement-tip__actions .btn i {
  margin-right: 4px;
  vertical-align: -1px;
}

.commerce-bank-transfer-block {
  margin-top: 16px;
  padding: 16px 18px;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  background: #fafafa;
}

.commerce-bank-transfer-block__title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 8px;
  font-size: 15px;
  font-weight: 600;
  color: #111827;
}

.commerce-bank-transfer-block__title i {
  font-size: 18px;
  color: #059669;
}

.commerce-bank-transfer-block__lead {
  margin-bottom: 14px;
}

.commerce-bank-transfer-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 16px;
}

@media (max-width: 768px) {
  .commerce-bank-transfer-grid {
    grid-template-columns: 1fr;
  }
}

.commerce-bank-transfer-field textarea.form-control {
  min-height: 72px;
  resize: vertical;
}

.commerce-bank-transfer-required-star {
  color: #dc2626;
  font-weight: 700;
  margin-right: 2px;
}

.commerce-bank-transfer-block__error {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-bottom: 12px;
}

.commerce-bank-transfer-block__error i {
  flex-shrink: 0;
  margin-top: 2px;
}

.commerce-bank-transfer-field--invalid .form-control {
  border-color: #dc2626;
}

.commerce-bank-transfer-field--invalid .form-label {
  color: #991b1b;
}

.settlement-modes-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 6px;
}

.settlement-mode-option {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.settlement-mode-option:has(input:checked) {
  border-color: #667eea;
  background: #f5f3ff;
}

.settlement-mode-option--disabled {
  opacity: 0.55;
  cursor: not-allowed;
  background: #f9fafb;
}

.settlement-mode-option--disabled input[type='checkbox'] {
  cursor: not-allowed;
}

.settlement-mode-option input[type="checkbox"] {
  margin-top: 2px;
}

.settlement-mode-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.settlement-mode-label {
  font-size: 14px;
  font-weight: 500;
  color: #111827;
}

.settlement-mode-desc {
  font-size: 12px;
  color: #6b7280;
}

.order-capture-only-switch-row {
  display: flex;
  align-items: center;
  margin-top: 6px;
  margin-bottom: 4px;
}

.add-provider-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-end;
}

.ecpay-sandbox-fill {
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px dashed #e5e7eb;
}

/* 與編輯商品頁「物流溫層／上架狀態」相同的 radio 列樣式 */
.status-radio-group {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.status-radio-group .form-check-inline {
  margin-right: 0;
}

.credential-input-row {
  display: flex;
  align-items: stretch;
  max-width: 100%;
}

.credential-input-row.has-reveal .form-control {
  flex: 1;
  min-width: 0;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-right: none;
}

.credential-mask {
  -webkit-text-security: disc;
}

.btn-credential-reveal {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 0 6px 6px 0;
  background: #f9fafb;
  color: #6b7280;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.btn-credential-reveal:hover {
  background: #f3f4f6;
  color: #374151;
}

.commerce-delete-provider-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(15, 23, 42, 0.45);
}

.commerce-delete-provider-dialog {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.18);
  overflow: hidden;
}

.commerce-delete-provider-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px;
  border-bottom: 1px solid #e5e7eb;
}

.commerce-delete-provider-header h3 {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  color: #111827;
}

.commerce-delete-provider-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.commerce-delete-provider-close:hover {
  background: #f3f4f6;
  color: #111827;
}

.commerce-delete-provider-body {
  padding: 18px 20px;
  font-size: 14px;
  line-height: 1.55;
  color: #374151;
}

.commerce-delete-provider-body p {
  margin: 0 0 10px;
}

.commerce-delete-provider-body p:last-child {
  margin-bottom: 0;
}

.commerce-delete-provider-enabled-note {
  font-size: 13px;
  color: #1d4ed8;
}

.commerce-delete-provider-warning {
  font-size: 13px;
  color: #b45309;
}

.commerce-delete-provider-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 18px;
  border-top: 1px solid #e5e7eb;
  background: #fafafa;
}
</style>

<style>
.page-header-commerce-settings {
  position: fixed;
  top: 56px;
  left: 260px;
  right: 0;
  z-index: 100;
}
.sidebar.collapsed ~ .main-content .page-header-commerce-settings {
  left: 64px;
}
@media (max-width: 768px) {
  .page-header-commerce-settings {
    left: 0 !important;
    top: 56px;
  }
}
</style>

<style scoped>
/* 主題選擇 */
.theme-selector { display: flex; gap: 12px; flex-wrap: wrap; }
.theme-card {
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  cursor: pointer; padding: 6px; border-radius: 8px;
  border: 2px solid transparent; transition: border-color 0.15s;
}
.theme-card:hover, .theme-card.active { border-color: #667eea; }
.theme-radio { display: none; }
.theme-preview { width: 80px; height: 50px; border-radius: 6px; }
.theme-label { font-size: 12px; color: #374151; font-weight: 600; }
.theme-card .theme-desc {
  font-size: 11px; color: #6b7280; line-height: 1.35; text-align: center; max-width: 120px;
}
.theme-card {
  max-width: 132px;
}

.form-hint--warn { color: #b45309; }
.storefront-template-category-panel {
  margin-bottom: 20px; padding: 14px 14px 16px;
  border: 1px solid #e5e7eb; border-radius: 10px; background: #fafafa;
}
.storefront-template-category-head {
  display: flex; flex-wrap: wrap; align-items: baseline; justify-content: space-between; gap: 8px;
  margin-bottom: 6px;
}
.storefront-template-category-title {
  font-size: 15px; font-weight: 600; color: #111827; margin: 0;
}
.storefront-template-category-count {
  font-size: 12px; color: #6b7280; font-weight: 500; white-space: nowrap;
}
.storefront-template-category-desc { margin: 0 0 10px 0; }
.storefront-template-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 10px;
}
.storefront-template-card {
  display: flex; flex-direction: column; align-items: flex-start; gap: 6px;
  cursor: pointer; padding: 12px; border-radius: 8px; border: 2px solid #e5e7eb;
  background: #fafafa; transition: border-color 0.15s, background 0.15s;
  position: relative;
}
.storefront-template-card:hover { border-color: #c7d2fe; background: #f8fafc; }
.storefront-template-card.active { border-color: #667eea; background: #f5f3ff; }
.storefront-template-card.disabled {
  opacity: 0.56;
  cursor: not-allowed;
  background: #f3f4f6;
}
.storefront-template-card.disabled:hover {
  border-color: #e5e7eb;
  background: #f3f4f6;
}
.storefront-template-card-title { font-size: 14px; font-weight: 600; color: #111827; }
.storefront-template-card-desc { font-size: 12px; color: #6b7280; line-height: 1.45; }
.storefront-template-card-badge {
  align-self: flex-end;
  font-size: 11px;
  font-weight: 600;
  color: #92400e;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 999px;
  padding: 1px 8px;
}

/* 選單編輯 */
.empty-hint { color: #9ca3af; font-size: 13px; padding: 12px 0; }
.menu-item-row { display: flex; gap: 8px; align-items: flex-start; padding: 10px 0; border-bottom: 1px solid #f3f4f6; }
.menu-item-fields { display: flex; flex-wrap: wrap; gap: 8px; flex: 1; }
.form-group-inline { display: flex; flex-direction: column; gap: 2px; min-width: 120px; }
.form-label-sm { font-size: 11px; color: #6b7280; }
.toggle-row-sm { display: flex; align-items: center; gap: 4px; font-size: 12px; cursor: pointer; margin-top: 14px; }
.menu-item-actions { display: flex; flex-direction: column; gap: 4px; margin-top: 14px; }
.menu-items-toolbar { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }
.menu-default-hint,
.footer-default-hint { margin: 0 0 8px 0; }
.btn-icon {
  background: none; border: 1px solid #e5e7eb; border-radius: 4px;
  padding: 2px 6px; cursor: pointer; color: #374151; font-size: 14px;
  transition: background 0.1s;
}
.btn-icon:hover { background: #f3f4f6; }
.btn-icon--danger { color: #ef4444; border-color: #fecaca; }
.btn-icon--danger:hover { background: #fff1f2; }
.mt-1 { margin-top: 4px; }
.mt-2 { margin-top: 8px; }

/* Footer */
.footer-col-block { border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; margin-bottom: 10px; }
.footer-col-header { display: flex; gap: 8px; align-items: center; margin-bottom: 8px; }
.footer-col-title { flex: 1; }
.footer-link-row { display: flex; gap: 6px; align-items: center; margin-bottom: 6px; }

/* 時段 */
.time-slot-row { display: flex; gap: 6px; align-items: center; margin-bottom: 6px; }
.slot-sep { color: #6b7280; font-size: 12px; }

/* 指定出貨日 */
.lead-days-row { display: flex; align-items: center; gap: 8px; }
.lead-days-input { width: 80px !important; }
.lead-days-unit { font-size: 13px; color: #374151; }
</style>
